import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  CodeSnippet,
  Content,
  InlineLoading,
  InlineNotification,
  Modal,
  PasswordInput,
  Toggle,
  Select,
  SelectItem,
  Stack,
  Tag,
  TextArea,
  TextInput,
  Tile
} from '@carbon/react';
import { Close, CodeBlock, Copy, Dashboard, Document, Download, Edit, Help, Login, LogoGithub, Logout, Save, TrashCan, Upload, Watson } from '@carbon/icons-react';
import { MemberPreview } from './components/MemberPreview';
import type { AdminLlmPromptConfig, AdminOperationsResponse, AuthUser, BuildInfo, BulkHistoryOperationResponse, DatasetScanTask, GitHubAppRepository, GitRepositoryDataset, GitRepositoryDatasetDetail, GitSyncJob, LlmAnalysisResult, SessionPayload, UploadHistoryItem, UploadResponse, UserGitConfig, XmiMember } from './types';

const ENCODING_OPTIONS = [
  'auto',
  'CP037',
  'CP1047',
  'CP1140',
  'CP937',
  'CP935',
  'CP1388',
  'CP930',
  'CP939',
  'CP1390'
];

const DIRECTORY_PICKER_PROPS = {
  directory: '',
  webkitdirectory: ''
};

const SUPPORT_EMAIL = 'xmi_viewer@163.com';
const SAVED_MEMBER_FILTERS_KEY = 'xmidump.savedMemberFilters.v1';

type PublicPageKey = 'guide' | 'privacy' | 'terms';
type AdminPanelView = 'overview' | 'users' | 'uploads' | 'prompt' | 'logs';
type WorkspacePageKey = 'workspace' | 'datasets';

interface PublicPageSection {
  title: string;
  items: string[];
}

interface PublicPageContent {
  label: string;
  title: string;
  intro: string;
  updatedAt: string;
  sections: PublicPageSection[];
}

const PUBLIC_PAGE_CONTENT: Record<PublicPageKey, PublicPageContent> = {
  guide: {
    label: 'First-use guide',
    title: 'Using XMI Workbench',
    intro: 'A practical guide for uploading XMI/XMIT archives, browsing extracted members, saving AI output, managing history, and syncing datasets to GitHub.',
    updatedAt: 'June 20, 2026',
    sections: [
      {
        title: 'Upload and decode',
        items: [
          'Sign in, choose Upload XMI, then drop or choose a .xmi or .xmit file.',
          'Leave encoding on Auto detect for most English, Chinese, and Japanese datasets. If the preview text is wrong, upload again with the exact code page.',
          'The server keeps the original uploaded file for new uploads so the same history item can be reparsed after parser fixes.'
        ]
      },
      {
        title: 'Search and inspect members',
        items: [
          'Use the member search field to filter by member name. Enable Search content when you need to find text inside extracted members.',
          'Select a member from the list to preview syntax-highlighted text, copy the active member, download a ZIP, or save a small manual edit.',
          'Use the reparse action on retained-source uploads when parser or encoding behavior changes.'
        ]
      },
      {
        title: 'AI analysis',
        items: [
          'Use Analyze for the active member when you need a focused explanation or change summary.',
          'Use Scan for a dataset overview and member summaries. Scan runs as a background job and updates while it progresses.',
          'Saved AI outputs stay attached to the upload history item and are included in ZIP summaries and Git sync output.'
        ]
      },
      {
        title: 'History and deletion',
        items: [
          'Open History to return to prior uploads, saved AI outputs, parser warnings, scan jobs, and Git sync jobs for the current account.',
          'Duplicate uploads are allowed, but the UI warns when the same file content already exists in your history.',
          'Deleting a history item removes its upload record, retained source file, saved analyses, dataset scan tasks, Git sync jobs, and queued work for that upload.'
        ]
      },
      {
        title: 'GitHub sync',
        items: [
          'Open Git settings, connect the site GitHub App, refresh repositories, choose an authorized repository, and save branch/path settings.',
          'Use Sync dataset to Git to write member text, AI outputs, and a manifest into the selected repository.',
          'Enable Auto sync only when every new upload should immediately create or update Git content in the configured repository.'
        ]
      }
    ]
  },
  privacy: {
    label: 'Privacy',
    title: 'Privacy and data handling',
    intro: 'This page explains what the web app stores, when data leaves the server, and what deletion does.',
    updatedAt: 'June 20, 2026',
    sections: [
      {
        title: 'Account and session data',
        items: [
          'The app stores registered email addresses, salted password hashes, active sessions, and pending verification codes in the configured AUTH_DATA_DIR.',
          'The optional built-in admin account is read from ADMIN_EMAIL and ADMIN_PASSWORD environment variables and is not stored in user data files.',
          'Session cookies are HTTP-only and are used to keep browser requests associated with the signed-in account.'
        ]
      },
      {
        title: 'Uploaded files and parsed data',
        items: [
          'Uploaded XMI/XMIT files are parsed on the server and stored as private upload history for the current account.',
          'For new uploads, the original source file is retained in the configured upload storage directory so it can be reparsed later.',
          'Parsed member text, dataset metadata, parser warnings, saved member edits, AI outputs, scan tasks, and Git sync jobs are stored in local SQLite databases.'
        ]
      },
      {
        title: 'AI processing',
        items: [
          'Member analysis and dataset scans send relevant dataset text to the configured LLM endpoint only after a user starts an analysis or scan.',
          'The operator controls the provider, base URL, model, proxy, and API key through server configuration.',
          'AI outputs can be inaccurate and should be reviewed before use in operations, code changes, or production decisions.'
        ]
      },
      {
        title: 'GitHub permissions',
        items: [
          'GitHub sync uses the configured GitHub App and only repositories authorized during GitHub installation.',
          'The app stores the GitHub installation id, selected repository, branch, path prefix, commit author settings, and auto-sync preference.',
          'The server creates short-lived installation access tokens when it needs to read repositories or push synced dataset content.'
        ]
      },
      {
        title: 'Deletion and backups',
        items: [
          'Deleting a history item removes the upload record, retained original file, saved analyses, scan tasks, Git sync jobs, and queued work for that upload.',
          'Backups may retain older copies until the operator backup rotation removes them.',
          'For account-level deletion or export requests, contact support at xmi_viewer@163.com.'
        ]
      }
    ]
  },
  terms: {
    label: 'Terms',
    title: 'Terms of use',
    intro: 'These terms set the operating boundaries for using XMI Workbench in a local or team deployment.',
    updatedAt: 'June 20, 2026',
    sections: [
      {
        title: 'Authorized use',
        items: [
          'Use the app only with XMI/XMIT files, GitHub repositories, credentials, and AI endpoints that you are authorized to access.',
          'Do not upload secrets, regulated data, or third-party data unless your deployment, provider contracts, and operating policies allow it.',
          'You are responsible for reviewing extracted text, AI output, and Git commits before using them in operational decisions.'
        ]
      },
      {
        title: 'AI output',
        items: [
          'AI analysis is generated by the configured provider and can contain omissions, mistakes, or unsupported recommendations.',
          'AI output is saved for convenience and auditability, but it is not a substitute for code review, security review, or mainframe operations review.',
          'Provider availability, latency, model behavior, and retention outside this app depend on the configured AI endpoint.'
        ]
      },
      {
        title: 'GitHub sync',
        items: [
          'When you run Git sync or enable Auto sync, the app can create commits in the selected authorized repository.',
          'Repository permissions are controlled through the GitHub App installation and can be changed or revoked in GitHub.',
          'Review branch, path prefix, and auto-sync settings before enabling automatic writes.'
        ]
      },
      {
        title: 'Service operation',
        items: [
          'The app is provided for local or team use and depends on server configuration, storage, SMTP, GitHub, and AI provider availability.',
          'The operator is responsible for configuring backups, restore testing, secrets, TLS, reverse proxy limits, and access controls.',
          'The app may record audit events and operational errors to support security review and troubleshooting.'
        ]
      },
      {
        title: 'Support and changes',
        items: [
          'For support, deletion, export, or policy questions, contact xmi_viewer@163.com.',
          'These terms may change as deployment, storage, AI, or GitHub sync behavior changes.',
          'Continuing to use the app after updates means you accept the current terms for that deployment.'
        ]
      }
    ]
  }
};

interface HistoryGroup {
  key: string;
  datasetName: string;
  items: UploadHistoryItem[];
  latestUploadedAt: string;
  totalMembers: number;
  totalAnalyses: number;
}

interface PendingHistoryDeletion {
  ids: string[];
  label: string;
  mode: 'single' | 'bulk';
}

interface MemberAnalysisGroup {
  memberName: string;
  latest: LlmAnalysisResult;
  latestSummary: LlmAnalysisResult | null;
  latestDetail: LlmAnalysisResult | null;
  total: number;
}

interface SavedMemberFilter {
  id: string;
  label: string;
  query: string;
  searchContent: boolean;
}

interface MemberSearchIndexEntry {
  member: XmiMember;
  lowerName: string;
  lowerText: string;
  lowerLines: string[];
}

export default function App() {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const publicPageKey = getPublicPageKey(pathname);
  const isAdminPage = pathname.replace(/\/+$/, '') === '/admin';
  const activeWorkspacePage = getWorkspacePageKey(pathname);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isAuthSubmitting, setIsAuthSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authInfo, setAuthInfo] = useState<string | null>(null);

  const [workspacePane, setWorkspacePane] = useState<'members' | 'analyses'>('members');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isPersonalSettingsOpen, setIsPersonalSettingsOpen] = useState(false);
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [historyItems, setHistoryItems] = useState<UploadHistoryItem[]>([]);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [selectedHistoryIds, setSelectedHistoryIds] = useState<string[]>([]);
  const [activeMemberName, setActiveMemberName] = useState<string | null>(null);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(null);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [searchMemberContent, setSearchMemberContent] = useState(false);
  const [savedMemberFilters, setSavedMemberFilters] = useState<SavedMemberFilter[]>(() => loadSavedMemberFilters());
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const [scanTasks, setScanTasks] = useState<DatasetScanTask[]>([]);
  const [gitSyncJobs, setGitSyncJobs] = useState<GitSyncJob[]>([]);
  const [gitRepositoryDatasets, setGitRepositoryDatasets] = useState<GitRepositoryDataset[]>([]);
  const [selectedGitDataset, setSelectedGitDataset] = useState<GitRepositoryDatasetDetail | null>(null);
  const [gitHubRepositories, setGitHubRepositories] = useState<GitHubAppRepository[]>([]);
  const [selectedGitHubRepository, setSelectedGitHubRepository] = useState('');
  const [isGitHubAppConfigured, setIsGitHubAppConfigured] = useState(false);
  const [encoding, setEncoding] = useState('auto');

  const [gitHubInstallationId, setGitHubInstallationId] = useState<number | undefined>();
  const [gitRepositoryOwner, setGitRepositoryOwner] = useState('');
  const [gitRepositoryName, setGitRepositoryName] = useState('');
  const [gitRepositoryHtmlUrl, setGitRepositoryHtmlUrl] = useState('');
  const [gitRepositoryUrl, setGitRepositoryUrl] = useState('');
  const [gitBranch, setGitBranch] = useState('main');
  const [gitPathPrefix, setGitPathPrefix] = useState('');
  const [gitAuthorName, setGitAuthorName] = useState('');
  const [gitAuthorEmail, setGitAuthorEmail] = useState('');
  const [gitAutoSync, setGitAutoSync] = useState(false);
  const [isGitToolsOpen, setIsGitToolsOpen] = useState(false);
  const [adminPanelView, setAdminPanelView] = useState<AdminPanelView>('overview');
  const [adminPromptProviderId, setAdminPromptProviderId] = useState('');
  const [adminPromptBaseUrl, setAdminPromptBaseUrl] = useState('');
  const [adminPromptModel, setAdminPromptModel] = useState('');
  const [adminPromptScope, setAdminPromptScope] = useState<'member' | 'dataset'>('member');
  const [adminPromptText, setAdminPromptText] = useState('');

  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isAdminOperationsLoading, setIsAdminOperationsLoading] = useState(false);
  const [isOpeningHistoryId, setIsOpeningHistoryId] = useState<string | null>(null);
  const [deletingHistoryId, setDeletingHistoryId] = useState<string | null>(null);
  const [bulkHistoryAction, setBulkHistoryAction] = useState<'delete' | 'reparse' | null>(null);
  const [pendingHistoryDeletion, setPendingHistoryDeletion] = useState<PendingHistoryDeletion | null>(null);
  const [duplicateUploadNotice, setDuplicateUploadNotice] = useState<UploadHistoryItem | null>(null);
  const [analysisAction, setAnalysisAction] = useState<'member' | 'scan' | null>(null);
  const [gitAction, setGitAction] = useState<'save' | 'sync' | 'retry' | 'read' | 'detail' | 'install' | 'repos' | null>(null);
  const [analysisTargetMemberName, setAnalysisTargetMemberName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isReparsing, setIsReparsing] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'done'>('idle');
  const [isEditingMember, setIsEditingMember] = useState(false);
  const [memberEditText, setMemberEditText] = useState('');
  const [isSavingMember, setIsSavingMember] = useState(false);
  const [isSavingAdminPrompt, setIsSavingAdminPrompt] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyInfo, setHistoryInfo] = useState<string | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [adminOperationsError, setAdminOperationsError] = useState<string | null>(null);
  const [gitError, setGitError] = useState<string | null>(null);
  const [gitInfo, setGitInfo] = useState<string | null>(null);
  const [adminInfo, setAdminInfo] = useState<string | null>(null);
  const [memberSaveError, setMemberSaveError] = useState<string | null>(null);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [adminOperations, setAdminOperations] = useState<AdminOperationsResponse | null>(null);
  const xmiFileInputRef = useRef<HTMLInputElement | null>(null);
  const txtFolderInputRef = useRef<HTMLInputElement | null>(null);

  const members = session?.summary.members ?? [];
  const normalizedHistorySearch = historySearchQuery.trim().toLowerCase();
  const deferredMemberSearchQuery = useDeferredValue(memberSearchQuery);
  const normalizedMemberSearch = deferredMemberSearchQuery.trim().toLowerCase();
  const normalizedImmediateMemberSearch = memberSearchQuery.trim().toLowerCase();
  const filteredHistoryItems = useMemo(() => {
    if (!normalizedHistorySearch) {
      return historyItems;
    }
    return historyItems.filter(item =>
      item.datasetName.toLowerCase().includes(normalizedHistorySearch)
      || item.fileName.toLowerCase().includes(normalizedHistorySearch)
    );
  }, [historyItems, normalizedHistorySearch]);
  const groupedHistoryItems = useMemo<HistoryGroup[]>(() => {
    const groups = new Map<string, HistoryGroup>();

    for (const item of filteredHistoryItems) {
      const existing = groups.get(item.datasetName);
      if (existing) {
        existing.items.push(item);
        existing.totalMembers += item.memberCount;
        existing.totalAnalyses += item.analysisCount;
        if (item.uploadedAt > existing.latestUploadedAt) {
          existing.latestUploadedAt = item.uploadedAt;
        }
        continue;
      }

      groups.set(item.datasetName, {
        key: item.datasetName,
        datasetName: item.datasetName,
        items: [item],
        latestUploadedAt: item.uploadedAt,
        totalMembers: item.memberCount,
        totalAnalyses: item.analysisCount
      });
    }

    return [...groups.values()];
  }, [filteredHistoryItems]);
  const filteredHistoryIds = useMemo(() => filteredHistoryItems.map(item => item.id), [filteredHistoryItems]);
  const selectedHistoryItems = useMemo(
    () => historyItems.filter(item => selectedHistoryIds.includes(item.id)),
    [historyItems, selectedHistoryIds]
  );
  const selectedVisibleHistoryIds = useMemo(
    () => filteredHistoryIds.filter(id => selectedHistoryIds.includes(id)),
    [filteredHistoryIds, selectedHistoryIds]
  );
  const selectableReparseCount = selectedHistoryItems.filter(item => item.originalStored).length;
  const isBulkHistoryBusy = bulkHistoryAction !== null;
  const retainedHistoryCount = useMemo(
    () => historyItems.filter(item => item.originalStored).length,
    [historyItems]
  );
  const totalSavedHistoryAnalyses = useMemo(
    () => historyItems.reduce((total, item) => total + item.analysisCount, 0),
    [historyItems]
  );
  const memberSearchIndex = useMemo<MemberSearchIndexEntry[]>(
    () => members.map(member => buildMemberSearchIndexEntry(member)),
    [members]
  );
  const searchableMembers = useMemo(() => {
    if (!normalizedMemberSearch) {
      return memberSearchIndex.map(entry => ({
        member: entry.member,
        nameMatched: false,
        contentMatched: false,
        matchedLineNumbers: [] as number[]
      }));
    }

    return memberSearchIndex.map(entry => {
      const nameMatched = entry.lowerName.includes(normalizedMemberSearch);
      const matchedLineNumbers = searchMemberContent
        ? findMatchingLineNumbersFromIndex(entry, normalizedMemberSearch)
        : [];
      return {
        member: entry.member,
        nameMatched,
        contentMatched: matchedLineNumbers.length > 0,
        matchedLineNumbers
      };
    }).filter(result => searchMemberContent
      ? result.nameMatched || result.contentMatched
      : result.nameMatched);
  }, [memberSearchIndex, normalizedMemberSearch, searchMemberContent]);
  const visibleMembers = useMemo(() => searchableMembers.map(result => result.member), [searchableMembers]);
  const isMemberSearchPending = normalizedImmediateMemberSearch !== normalizedMemberSearch;
  const canSaveMemberFilter = normalizedImmediateMemberSearch.length > 0
    && !savedMemberFilters.some(filter =>
      filter.query.toLowerCase() === normalizedImmediateMemberSearch
      && filter.searchContent === searchMemberContent);
  const analyses = session?.analyses ?? [];
  const sortedAnalyses = useMemo(
    () => [...analyses].sort((left, right) => right.createdAt.localeCompare(left.createdAt)),
    [analyses]
  );
  const latestSavedAnalysisByMember = useMemo(() => {
    const map = new Map<string, LlmAnalysisResult>();

    for (const analysis of sortedAnalyses) {
      if (analysis.kind !== 'member_summary' || !analysis.memberName || map.has(analysis.memberName)) {
        continue;
      }
      map.set(analysis.memberName, analysis);
    }

    for (const analysis of sortedAnalyses) {
      if (!analysis.memberName || map.has(analysis.memberName)) {
        continue;
      }
      map.set(analysis.memberName, analysis);
    }

    return map;
  }, [sortedAnalyses]);
  const memberAnalysisGroups = useMemo<MemberAnalysisGroup[]>(() => {
    const groups = new Map<string, MemberAnalysisGroup>();

    for (const analysis of sortedAnalyses) {
      if (!analysis.memberName) {
        continue;
      }

      const existing = groups.get(analysis.memberName);
      if (existing) {
        existing.total += 1;
        if (!existing.latestSummary && analysis.kind === 'member_summary') {
          existing.latestSummary = analysis;
        }
        if (!existing.latestDetail && analysis.kind === 'member_detail') {
          existing.latestDetail = analysis;
        }
        continue;
      }

      groups.set(analysis.memberName, {
        memberName: analysis.memberName,
        latest: analysis,
        latestSummary: analysis.kind === 'member_summary' ? analysis : null,
        latestDetail: analysis.kind === 'member_detail' ? analysis : null,
        total: 1
      });
    }

    return [...groups.values()].sort((left, right) => right.latest.createdAt.localeCompare(left.latest.createdAt));
  }, [sortedAnalyses]);
  const datasetOverviewAnalysis = useMemo(
    () => sortedAnalyses.find(analysis => analysis.kind === 'dataset_overview') ?? null,
    [sortedAnalyses]
  );
  const latestScanTask = scanTasks[0] ?? null;
  const activeScanTask = scanTasks.find(task => task.status === 'queued' || task.status === 'running') ?? null;
  const latestGitSyncJob = gitSyncJobs[0] ?? null;
  const activeGitSyncJob = gitSyncJobs.find(job => job.status === 'queued' || job.status === 'running') ?? null;
  const latestFailedGitSyncJob = gitSyncJobs.find(job => job.status === 'failed') ?? null;
  const isGitConfigured = Boolean(gitHubInstallationId) && gitRepositoryUrl.trim().length > 0;
  const activeMember = useMemo(() => {
    if (!activeMemberName) {
      return null;
    }
    return visibleMembers.find(member => member.name === activeMemberName) ?? null;
  }, [activeMemberName, visibleMembers]);
  const activeMemberSearchResult = useMemo(
    () => searchableMembers.find(result => result.member.name === activeMember?.name),
    [activeMember?.name, searchableMembers]
  );
  const activeMemberSavedAnalysis = useMemo(
    () => activeMember ? latestSavedAnalysisByMember.get(activeMember.name) ?? null : null,
    [activeMember, latestSavedAnalysisByMember]
  );
  const isMemberEditDirty = activeMember ? memberEditText !== activeMember.text : false;
  const memberEditorLineCount = memberEditText.length === 0 ? 0 : memberEditText.split('\n').length;
  const activeMemberMatchLines = activeMemberSearchResult?.matchedLineNumbers ?? [];
  const activeMatchLineNumber = activeMemberMatchLines[activeMatchIndex] ?? null;
  const selectedAnalysis = useMemo(() => {
    if (analyses.length === 0) {
      return null;
    }
    return analyses.find(analysis => analysis.id === selectedAnalysisId) ?? analyses[analyses.length - 1];
  }, [analyses, selectedAnalysisId]);
  const latestAnalysis = analyses[analyses.length - 1] ?? null;
  const parserIssues = (session?.summary.warnings ?? []).filter(isParserIssue);

  useEffect(() => {
    const handlePopState = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    void loadBuildInfo();
    void loadCurrentUser();
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }
    void loadHistory();
    void loadGitConfig();
  }, [user]);

  useEffect(() => {
    if (!user?.isAdmin || !isAdminPage) {
      return;
    }
    void loadAdminOperations();
  }, [isAdminPage, user?.isAdmin]);

  useEffect(() => {
    const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('xmidump-github-app');

    function handleGitHubAppMessage(event: MessageEvent) {
      if (event.data?.type !== 'xmidump:github-app-installed') {
        return;
      }
      void loadGitConfig();
      void handleLoadGitHubRepositories();
    }

    window.addEventListener('message', handleGitHubAppMessage);
    channel?.addEventListener('message', handleGitHubAppMessage);
    return () => {
      window.removeEventListener('message', handleGitHubAppMessage);
      channel?.close();
    };
  }, [gitRepositoryOwner, gitRepositoryName]);

  useEffect(() => {
    setSelectedHistoryIds(previous => {
      if (previous.length === 0) {
        return previous;
      }
      const existingIds = new Set(historyItems.map(item => item.id));
      const next = previous.filter(id => existingIds.has(id));
      return next.length === previous.length ? previous : next;
    });
  }, [historyItems]);

  useEffect(() => {
    persistSavedMemberFilters(savedMemberFilters);
  }, [savedMemberFilters]);

  useEffect(() => {
    if (!session) {
      setActiveMemberName(null);
      setSelectedAnalysisId(null);
      setScanTasks([]);
      setGitSyncJobs([]);
      return;
    }
    if (normalizedMemberSearch) {
      if (activeMemberName && !visibleMembers.some(member => member.name === activeMemberName)) {
        setActiveMemberName(null);
      }
      return;
    }
    if (members.length === 0) {
      setActiveMemberName(null);
    } else if (!activeMemberName || !members.some(member => member.name === activeMemberName)) {
      setActiveMemberName(members[0].name);
    }
  }, [activeMemberName, members, normalizedMemberSearch, session, visibleMembers]);

  useEffect(() => {
    if (analyses.length === 0) {
      setSelectedAnalysisId(null);
      return;
    }

    if (!selectedAnalysisId || !analyses.some(analysis => analysis.id === selectedAnalysisId)) {
      setSelectedAnalysisId(analyses[analyses.length - 1].id);
    }
  }, [analyses, selectedAnalysisId]);

  useEffect(() => {
    setActiveMatchIndex(0);
  }, [activeMember?.name, normalizedMemberSearch, searchMemberContent]);

  useEffect(() => {
    setIsEditingMember(false);
    setMemberEditText(activeMember?.text ?? '');
    setMemberSaveError(null);
  }, [activeMember?.name, session?.sessionId]);

  useEffect(() => {
    if (!session) {
      return;
    }
    void loadScanTasks(session.sessionId);
    void loadGitSyncJobs(session.sessionId);
  }, [session?.sessionId]);

  useEffect(() => {
    if (!session || !activeScanTask) {
      return;
    }

    const timer = window.setInterval(() => {
      void refreshScanTask(session.sessionId, activeScanTask.id);
    }, 2500);

    return () => window.clearInterval(timer);
  }, [activeScanTask?.id, session?.sessionId]);

  useEffect(() => {
    if (!session || !activeGitSyncJob) {
      return;
    }

    const timer = window.setInterval(() => {
      void refreshGitSyncJob(session.sessionId, activeGitSyncJob.id);
    }, 2500);

    return () => window.clearInterval(timer);
  }, [activeGitSyncJob?.id, session?.sessionId]);

  useEffect(() => {
    if (activeGitSyncJob || gitError || gitRepositoryDatasets.length > 0 || selectedGitDataset) {
      setIsGitToolsOpen(true);
    }
  }, [activeGitSyncJob, gitError, gitRepositoryDatasets.length, selectedGitDataset]);

  async function loadCurrentUser() {
    try {
      const payload = await apiJson<{ user: AuthUser | null }>('/api/auth/me');
      setUser(payload.user);
    } catch {
      setUser(null);
    } finally {
      setIsAuthLoading(false);
    }
  }

  async function loadBuildInfo() {
    try {
      const payload = await apiJson<BuildInfo>('/api/version');
      setBuildInfo(payload);
    } catch {
      setBuildInfo(null);
    }
  }

  async function loadHistory() {
    setIsHistoryLoading(true);
    setHistoryError(null);

    try {
      const payload = await apiJson<{ items: UploadHistoryItem[] }>('/api/history');
      setHistoryItems(payload.items);
    } catch (historyFailure) {
      setHistoryError(historyFailure instanceof Error ? historyFailure.message : 'Failed to load history.');
    } finally {
      setIsHistoryLoading(false);
    }
  }

  async function loadGitConfig() {
    try {
      const payload = await apiJson<{ config: UserGitConfig | null; githubAppConfigured?: boolean }>('/api/git/config');
      applyGitConfig(payload.config);
      setIsGitHubAppConfigured(Boolean(payload.githubAppConfigured));
    } catch {
      applyGitConfig(null);
      setIsGitHubAppConfigured(false);
    }
  }

  async function loadAdminOperations() {
    setIsAdminOperationsLoading(true);
    setAdminOperationsError(null);

    try {
      const payload = await apiJson<AdminOperationsResponse>('/api/admin/operations');
      setAdminOperations(payload);
      applyAdminPromptConfig(payload.llmPrompt);
    } catch (operationsError) {
      setAdminOperationsError(operationsError instanceof Error ? operationsError.message : 'Failed to load admin operations.');
    } finally {
      setIsAdminOperationsLoading(false);
    }
  }

  function applyAdminPromptConfig(config: AdminLlmPromptConfig) {
    setAdminPromptProviderId(config.providerId);
    setAdminPromptBaseUrl(config.baseUrl);
    setAdminPromptModel(config.model);
    setAdminPromptScope(config.scope);
    setAdminPromptText(config.prompt);
  }

  async function handleSaveAdminPrompt() {
    setIsSavingAdminPrompt(true);
    setAdminOperationsError(null);
    setAdminInfo(null);

    try {
      const payload = await apiJson<{ config: AdminLlmPromptConfig }>('/api/admin/llm-prompt', {
        method: 'PUT',
        body: JSON.stringify({
          providerId: adminPromptProviderId,
          baseUrl: adminPromptBaseUrl,
          model: adminPromptModel,
          scope: adminPromptScope,
          prompt: adminPromptText
        })
      });
      applyAdminPromptConfig(payload.config);
      setAdminOperations(previous => previous ? { ...previous, llmPrompt: payload.config } : previous);
      setAdminInfo('LLM prompt saved.');
    } catch (saveError) {
      setAdminOperationsError(saveError instanceof Error ? saveError.message : 'Failed to save LLM prompt.');
    } finally {
      setIsSavingAdminPrompt(false);
    }
  }

  async function loadScanTasks(sessionId: string) {
    try {
      const payload = await apiJson<{ tasks: DatasetScanTask[] }>(`/api/session/${sessionId}/scan-tasks`);
      setScanTasks(payload.tasks);
    } catch {
      setScanTasks([]);
    }
  }

  async function loadGitSyncJobs(sessionId: string) {
    try {
      const payload = await apiJson<{ jobs: GitSyncJob[] }>(`/api/session/${sessionId}/git-sync-jobs`);
      setGitSyncJobs(payload.jobs);
    } catch {
      setGitSyncJobs([]);
    }
  }

  async function refreshScanTask(sessionId: string, taskId: string) {
    try {
      const [taskPayload, sessionPayload] = await Promise.all([
        apiJson<{ task: DatasetScanTask }>(`/api/scan-task/${taskId}`),
        apiJson<SessionPayload>(`/api/session/${sessionId}`)
      ]);

      setScanTasks(previous => {
        const rest = previous.filter(task => task.id !== taskPayload.task.id);
        return [taskPayload.task, ...rest].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
      });
      setSession(sessionPayload);
      if (taskPayload.task.status === 'completed') {
        await loadHistory();
      }
    } catch {
      // Ignore transient polling failures.
    }
  }

  async function refreshGitSyncJob(sessionId: string, jobId: string) {
    try {
      const [jobPayload, sessionPayload] = await Promise.all([
        apiJson<{ job: GitSyncJob }>(`/api/git-sync-job/${jobId}`),
        apiJson<SessionPayload>(`/api/session/${sessionId}`)
      ]);

      setGitSyncJobs(previous => {
        const rest = previous.filter(job => job.id !== jobPayload.job.id);
        return [jobPayload.job, ...rest].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
      });
      setSession(sessionPayload);
      if (jobPayload.job.status === 'completed') {
        await loadHistory();
      }
    } catch {
      // Ignore transient polling failures.
    }
  }

  async function handleRequestCode() {
    setAuthError(null);
    setAuthInfo(null);
    setDevCode(null);
    setIsAuthSubmitting(true);

    try {
      const payload = await apiJson<{ ok: true; devCode?: string }>('/api/auth/register/request-code', {
        method: 'POST',
        body: JSON.stringify({ email: authEmail })
      });
      setDevCode(payload.devCode ?? null);
      setAuthInfo('Verification code sent.');
    } catch (requestError) {
      setAuthError(requestError instanceof Error ? requestError.message : 'Failed to send verification code.');
    } finally {
      setIsAuthSubmitting(false);
    }
  }

  async function handleRegister() {
    setAuthError(null);
    setAuthInfo(null);
    setIsAuthSubmitting(true);

    try {
      const payload = await apiJson<{ user: AuthUser }>('/api/auth/register/complete', {
        method: 'POST',
        body: JSON.stringify({ email: authEmail, password: authPassword, code: authCode })
      });
      setUser(payload.user);
      resetAuthForm();
    } catch (registerError) {
      setAuthError(registerError instanceof Error ? registerError.message : 'Registration failed.');
    } finally {
      setIsAuthSubmitting(false);
    }
  }

  async function handleLogin() {
    setAuthError(null);
    setAuthInfo(null);
    setIsAuthSubmitting(true);

    try {
      const payload = await apiJson<{ user: AuthUser }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: authEmail, password: authPassword })
      });
      setUser(payload.user);
      resetAuthForm();
    } catch (loginError) {
      setAuthError(loginError instanceof Error ? loginError.message : 'Login failed.');
    } finally {
      setIsAuthSubmitting(false);
    }
  }

  async function handleLogout() {
    await apiJson<{ ok: true }>('/api/auth/logout', { method: 'POST' });
    setUser(null);
    setSession(null);
    setHistoryItems([]);
    setSelectedHistoryIds([]);
    setGitSyncJobs([]);
    setGitRepositoryDatasets([]);
    setSelectedGitDataset(null);
    setIsUploadModalOpen(false);
    setSelectedAnalysisId(null);
  }

  function resetAuthForm() {
    setAuthPassword('');
    setAuthCode('');
    setDevCode(null);
    setAuthError(null);
    setAuthInfo(null);
  }

  async function handleUpload(files: FileList | null, options: { datasetName?: string } = {}) {
    const uploadFiles = Array.from(files ?? []);
    if (uploadFiles.length === 0) {
      return;
    }

    setError(null);
    setHistoryError(null);
    setIsUploading(true);

    try {
      const formData = new FormData();
      uploadFiles.forEach(uploadFile => {
        formData.append('file', uploadFile);
        const relativePath = getFileRelativePath(uploadFile);
        if (relativePath) {
          formData.append('relativePath', relativePath);
        }
      });
      if (options.datasetName) {
        formData.append('datasetName', options.datasetName);
      }
      formData.append('encoding', encoding);

      const payload = await apiJsonWithTimeout<UploadResponse>('/api/upload', {
        method: 'POST',
        body: formData
      }, 60000);

      setSession(payload);
      setMemberSearchQuery('');
      setActiveMemberName(payload.summary.members[0]?.name ?? null);
      setSelectedAnalysisId(payload.analyses[payload.analyses.length - 1]?.id ?? null);
      setScanTasks([]);
      setGitSyncJobs([]);
      setDuplicateUploadNotice(payload.duplicateUpload ?? null);
      setAnalysisError(null);
      setIsUploadModalOpen(false);
      await loadHistory();
      await loadGitSyncJobs(payload.sessionId);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Upload failed.');
    } finally {
      setIsUploading(false);
    }
  }

  function handleTxtFolderSelection(files: FileList | null) {
    const selectedFiles = Array.from(files ?? []);
    if (selectedFiles.length === 0) {
      return;
    }

    const datasetName = inferDatasetNameFromFolderFiles(selectedFiles);
    void handleUpload(toFileList(selectedFiles), { datasetName });
    if (txtFolderInputRef.current) {
      txtFolderInputRef.current.value = '';
    }
  }

  function handleXmiFileSelection(files: FileList | null) {
    void handleUpload(files);
    if (xmiFileInputRef.current) {
      xmiFileInputRef.current.value = '';
    }
  }

  async function handleOpenHistory(uploadId: string) {
    setError(null);
    setHistoryError(null);
    setIsOpeningHistoryId(uploadId);

    try {
      const payload = await apiJson<SessionPayload>(`/api/history/${uploadId}`);
      setSession(payload);
      setMemberSearchQuery('');
      setActiveMemberName(payload.summary.members[0]?.name ?? null);
      setSelectedAnalysisId(payload.analyses[payload.analyses.length - 1]?.id ?? null);
      await loadScanTasks(payload.sessionId);
      await loadGitSyncJobs(payload.sessionId);
      navigateTo('/workspace');
    } catch (historyFailure) {
      setHistoryError(historyFailure instanceof Error ? historyFailure.message : 'Failed to open history item.');
    } finally {
      setIsOpeningHistoryId(null);
    }
  }

  async function handleDeleteHistory(uploadId: string) {
    setHistoryError(null);
    setHistoryInfo(null);
    setDeletingHistoryId(uploadId);

    try {
      await apiJson<{ ok: true }>(`/api/history/${uploadId}`, { method: 'DELETE' });
      setHistoryItems(previous => previous.filter(historyItem => historyItem.id !== uploadId));
      setSelectedHistoryIds(previous => previous.filter(id => id !== uploadId));
      if (session?.uploadId === uploadId) {
        setSession(null);
        setScanTasks([]);
        setGitSyncJobs([]);
        setSelectedAnalysisId(null);
        setActiveMemberName(null);
      }
      if (duplicateUploadNotice?.id === uploadId) {
        setDuplicateUploadNotice(null);
      }
      setPendingHistoryDeletion(current => current?.ids.includes(uploadId) ? null : current);
      await loadHistory();
    } catch (deleteError) {
      setHistoryError(deleteError instanceof Error ? deleteError.message : 'Failed to delete history item.');
    } finally {
      setDeletingHistoryId(null);
    }
  }

  function requestDeleteHistory(item: UploadHistoryItem) {
    setHistoryError(null);
    setHistoryInfo(null);
    setPendingHistoryDeletion({
      ids: [item.id],
      label: item.fileName,
      mode: 'single'
    });
  }

  function closeDeleteHistoryModal() {
    if (deletingHistoryId) {
      return;
    }
    setPendingHistoryDeletion(null);
  }

  function toggleHistorySelection(uploadId: string, checked: boolean) {
    setHistoryError(null);
    setHistoryInfo(null);
    setSelectedHistoryIds(previous => {
      if (checked) {
        return previous.includes(uploadId) ? previous : [...previous, uploadId];
      }
      return previous.filter(id => id !== uploadId);
    });
  }

  function selectVisibleHistoryItems() {
    setHistoryError(null);
    setHistoryInfo(null);
    setSelectedHistoryIds(previous => {
      const next = new Set(previous);
      for (const id of filteredHistoryIds) {
        next.add(id);
      }
      return [...next];
    });
  }

  function clearHistorySelection() {
    setSelectedHistoryIds([]);
  }

  function requestBulkDeleteHistory() {
    if (selectedHistoryIds.length === 0) {
      return;
    }
    setHistoryError(null);
    setHistoryInfo(null);
    setPendingHistoryDeletion({
      ids: selectedHistoryIds,
      label: `${selectedHistoryIds.length} selected upload${selectedHistoryIds.length === 1 ? '' : 's'}`,
      mode: 'bulk'
    });
  }

  async function handleBulkDeleteHistory(uploadIds: string[]) {
    if (uploadIds.length === 0) {
      return;
    }

    setHistoryError(null);
    setHistoryInfo(null);
    setBulkHistoryAction('delete');

    try {
      const payload = await apiJson<BulkHistoryOperationResponse>('/api/history/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ uploadIds })
      });
      setHistoryItems(payload.items);
      setSelectedHistoryIds(previous => previous.filter(id => !uploadIds.includes(id)));
      if (session && uploadIds.includes(session.uploadId)) {
        setSession(null);
        setScanTasks([]);
        setGitSyncJobs([]);
        setSelectedAnalysisId(null);
        setActiveMemberName(null);
      }
      if (duplicateUploadNotice && uploadIds.includes(duplicateUploadNotice.id)) {
        setDuplicateUploadNotice(null);
      }
      setPendingHistoryDeletion(null);
      const failedCount = payload.results.filter(result => !result.ok).length;
      setHistoryInfo(failedCount === 0
        ? `Deleted ${payload.results.length} upload${payload.results.length === 1 ? '' : 's'}.`
        : `Deleted ${payload.results.length - failedCount} upload${payload.results.length - failedCount === 1 ? '' : 's'}; ${failedCount} failed.`);
    } catch (deleteError) {
      setHistoryError(deleteError instanceof Error ? deleteError.message : 'Failed to delete selected history items.');
    } finally {
      setBulkHistoryAction(null);
    }
  }

  async function handleBulkReparseHistory() {
    const uploadIds = selectedHistoryItems.filter(item => item.originalStored).map(item => item.id);
    if (uploadIds.length === 0) {
      setHistoryError('Select at least one retained-source upload to reparse.');
      return;
    }

    setHistoryError(null);
    setHistoryInfo(null);
    setBulkHistoryAction('reparse');

    try {
      const payload = await apiJson<BulkHistoryOperationResponse>('/api/history/bulk-reparse', {
        method: 'POST',
        body: JSON.stringify({ uploadIds, encoding })
      });
      setHistoryItems(payload.items);
      if (session && uploadIds.includes(session.uploadId)) {
        const refreshed = await apiJson<SessionPayload>(`/api/history/${session.uploadId}`);
        setSession(refreshed);
        setActiveMemberName(refreshed.summary.members[0]?.name ?? null);
        setSelectedAnalysisId(refreshed.analyses[refreshed.analyses.length - 1]?.id ?? null);
      }
      const failedCount = payload.results.filter(result => !result.ok).length;
      setHistoryInfo(failedCount === 0
        ? `Reparsed ${payload.results.length} upload${payload.results.length === 1 ? '' : 's'}.`
        : `Reparsed ${payload.results.length - failedCount} upload${payload.results.length - failedCount === 1 ? '' : 's'}; ${failedCount} failed.`);
    } catch (reparseError) {
      setHistoryError(reparseError instanceof Error ? reparseError.message : 'Failed to reparse selected history items.');
    } finally {
      setBulkHistoryAction(null);
    }
  }

  function saveCurrentMemberFilter() {
    const query = memberSearchQuery.trim();
    if (!query) {
      return;
    }

    setSavedMemberFilters(previous => {
      const duplicate = previous.some(filter =>
        filter.query.toLowerCase() === query.toLowerCase()
        && filter.searchContent === searchMemberContent);
      if (duplicate) {
        return previous;
      }
      const next: SavedMemberFilter = {
        id: createClientId(),
        label: query.length > 28 ? `${query.slice(0, 27)}...` : query,
        query,
        searchContent: searchMemberContent
      };
      return [next, ...previous].slice(0, 12);
    });
  }

  function applyMemberFilter(filter: SavedMemberFilter) {
    setMemberSearchQuery(filter.query);
    setSearchMemberContent(filter.searchContent);
    setWorkspacePane('members');
  }

  function removeSavedMemberFilter(filterId: string) {
    setSavedMemberFilters(previous => previous.filter(filter => filter.id !== filterId));
  }

  async function handleCopy() {
    if (!activeMember) {
      return;
    }
    await navigator.clipboard.writeText(activeMember.text);
    setCopyState('done');
    window.setTimeout(() => setCopyState('idle'), 1200);
  }

  function handleStartMemberEdit() {
    if (!activeMember) {
      return;
    }
    setMemberEditText(activeMember.text);
    setMemberSaveError(null);
    setIsEditingMember(true);
  }

  function handleCancelMemberEdit() {
    setMemberEditText(activeMember?.text ?? '');
    setMemberSaveError(null);
    setIsEditingMember(false);
  }

  async function handleSaveMemberEdit() {
    if (!session || !activeMember) {
      return;
    }

    setIsSavingMember(true);
    setMemberSaveError(null);

    try {
      const payload = await apiJson<{ session: SessionPayload }>(`/api/session/${session.sessionId}/member`, {
        method: 'PUT',
        body: JSON.stringify({
          memberName: activeMember.name,
          text: memberEditText
        })
      });
      setSession(payload.session);
      setMemberEditText(payload.session.summary.members.find(member => member.name === activeMember.name)?.text ?? memberEditText);
      setIsEditingMember(false);
      await loadHistory();
    } catch (saveError) {
      setMemberSaveError(saveError instanceof Error ? saveError.message : 'Failed to save member.');
    } finally {
      setIsSavingMember(false);
    }
  }

  async function handleInstallGitHubApp() {
    setGitError(null);
    setGitInfo(null);
    setGitAction('install');

    try {
      const payload = await apiJson<{ url: string }>('/api/git/github-app/install-url');
      const popup = window.open(payload.url, '_blank', 'width=960,height=760');
      if (!popup) {
        window.location.href = payload.url;
        return;
      }
      setGitInfo('Finish the GitHub App page, then return here. If GitHub Save is disabled, the selection is already saved; click Connect again here if needed.');
    } catch (installError) {
      setGitError(installError instanceof Error ? installError.message : 'Failed to start GitHub App installation.');
    } finally {
      setGitAction(null);
    }
  }

  async function handleLoadGitHubRepositories() {
    setGitError(null);
    setGitInfo(null);
    setGitAction('repos');

    try {
      await loadGitConfig();
      const payload = await apiJson<{ repositories: GitHubAppRepository[] }>('/api/git/github-app/repositories');
      setGitHubRepositories(payload.repositories);
      const currentFullName = gitRepositoryOwner && gitRepositoryName ? `${gitRepositoryOwner}/${gitRepositoryName}` : '';
      const nextSelected = currentFullName || payload.repositories[0]?.fullName || '';
      setSelectedGitHubRepository(nextSelected);
      setGitInfo(`${payload.repositories.length} authorized repositor${payload.repositories.length === 1 ? 'y' : 'ies'} found.`);
    } catch (repositoriesError) {
      setGitError(repositoriesError instanceof Error ? repositoriesError.message : 'Failed to load GitHub repositories.');
    } finally {
      setGitAction(null);
    }
  }

  async function handleSaveGitHubRepository() {
    const repository = gitHubRepositories.find(item => item.fullName === selectedGitHubRepository);
    if (!repository) {
      setGitError('Select a GitHub repository first.');
      return;
    }

    setGitError(null);
    setGitInfo(null);
    setGitAction('save');

    try {
      const payload = await apiJson<{ config: UserGitConfig | null }>('/api/git/github-app/repository', {
        method: 'POST',
        body: JSON.stringify({
          owner: repository.owner,
          name: repository.name,
          branch: gitBranch || repository.defaultBranch,
          pathPrefix: gitPathPrefix,
          authorName: gitAuthorName,
          authorEmail: gitAuthorEmail,
          autoSync: gitAutoSync
        })
      });
      applyGitConfig(payload.config);
      setGitInfo('GitHub repository saved.');
    } catch (saveError) {
      setGitError(saveError instanceof Error ? saveError.message : 'Failed to save GitHub repository.');
    } finally {
      setGitAction(null);
    }
  }

  async function handleSyncToGit() {
    if (!session) {
      return;
    }

    setGitError(null);
    setGitInfo(null);
    setGitAction('sync');

    try {
      const payload = await apiJson<{ job: GitSyncJob }>(`/api/session/${session.sessionId}/git-sync`, {
        method: 'POST'
      });
      setGitSyncJobs(previous => {
        const rest = previous.filter(job => job.id !== payload.job.id);
        return [payload.job, ...rest].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
      });
      await refreshGitSyncJob(session.sessionId, payload.job.id);
      setGitInfo(payload.job.status === 'completed' ? 'Git sync finished.' : 'Git sync queued.');
    } catch (syncError) {
      setGitError(syncError instanceof Error ? syncError.message : 'Failed to start Git sync.');
    } finally {
      setGitAction(null);
    }
  }

  async function handleRetryGitSync(job: GitSyncJob) {
    if (!session) {
      return;
    }

    setGitError(null);
    setGitInfo(null);
    setGitAction('retry');

    try {
      const payload = await apiJson<{ job: GitSyncJob }>(`/api/git-sync-job/${job.id}/retry`, {
        method: 'POST'
      });
      setGitSyncJobs(previous => {
        const rest = previous.filter(item => item.id !== payload.job.id);
        return [payload.job, ...rest].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
      });
      await refreshGitSyncJob(session.sessionId, payload.job.id);
      setGitInfo('Git sync retry queued.');
    } catch (retryError) {
      setGitError(retryError instanceof Error ? retryError.message : 'Failed to retry Git sync.');
    } finally {
      setGitAction(null);
    }
  }

  async function handleReadGitDatasets() {
    setGitError(null);
    setGitInfo(null);
    setGitAction('read');

    try {
      const payload = await apiJson<{ datasets: GitRepositoryDataset[] }>('/api/git/datasets');
      setGitRepositoryDatasets(payload.datasets);
      setSelectedGitDataset(null);
      setGitInfo(`${payload.datasets.length} dataset${payload.datasets.length === 1 ? '' : 's'} found in Git.`);
    } catch (readError) {
      setGitError(readError instanceof Error ? readError.message : 'Failed to read Git repository.');
    } finally {
      setGitAction(null);
    }
  }

  async function handleReadGitDatasetDetail(datasetPath: string) {
    setGitError(null);
    setGitInfo(null);
    setGitAction('detail');

    try {
      const payload = await apiJson<{ dataset: GitRepositoryDatasetDetail }>(`/api/git/dataset?path=${encodeURIComponent(datasetPath)}`);
      setSelectedGitDataset(payload.dataset);
    } catch (readError) {
      setGitError(readError instanceof Error ? readError.message : 'Failed to read Git dataset.');
    } finally {
      setGitAction(null);
    }
  }

  function applyGitConfig(config: UserGitConfig | null) {
    setGitHubInstallationId(config?.githubInstallationId);
    setGitRepositoryOwner(config?.repositoryOwner ?? '');
    setGitRepositoryName(config?.repositoryName ?? '');
    setGitRepositoryHtmlUrl(config?.repositoryHtmlUrl ?? '');
    setGitRepositoryUrl(config?.repositoryUrl ?? '');
    setGitBranch(config?.branch ?? 'main');
    setGitPathPrefix(config?.pathPrefix ?? '');
    setGitAuthorName(config?.authorName ?? '');
    setGitAuthorEmail(config?.authorEmail ?? '');
    setGitAutoSync(config?.autoSync ?? false);
    setSelectedGitHubRepository(config?.repositoryOwner && config.repositoryName
      ? `${config.repositoryOwner}/${config.repositoryName}`
      : '');
  }

  function navigateTo(nextPath: string) {
    if (window.location.pathname === nextPath) {
      setPathname(nextPath);
      return;
    }
    window.history.pushState({}, '', nextPath);
    setPathname(nextPath);
  }

  async function handleManageDataset(item: UploadHistoryItem) {
    await handleOpenHistory(item.id);
  }

  async function handleReparseHistoryItem(item: UploadHistoryItem) {
    if (!item.originalStored) {
      setHistoryError('Original XMI file is not stored for this history item. Upload it again to reparse.');
      return;
    }

    setHistoryError(null);
    setHistoryInfo(null);
    setBulkHistoryAction('reparse');

    try {
      const payload = await apiJson<BulkHistoryOperationResponse>('/api/history/bulk-reparse', {
        method: 'POST',
        body: JSON.stringify({ uploadIds: [item.id], encoding })
      });
      setHistoryItems(payload.items);
      if (session?.uploadId === item.id) {
        const refreshed = await apiJson<SessionPayload>(`/api/history/${item.id}`);
        setSession(refreshed);
        setActiveMemberName(refreshed.summary.members[0]?.name ?? null);
        setSelectedAnalysisId(refreshed.analyses[refreshed.analyses.length - 1]?.id ?? null);
      }
      const result = payload.results[0];
      setHistoryInfo(result?.ok ? `Reparsed ${item.fileName}.` : (result?.error ?? 'Reparse failed.'));
    } catch (reparseError) {
      setHistoryError(reparseError instanceof Error ? reparseError.message : 'Failed to reparse history item.');
    } finally {
      setBulkHistoryAction(null);
    }
  }

  async function handleSyncHistoryItemToGit(item: UploadHistoryItem) {
    setGitError(null);
    setGitInfo(null);

    try {
      const payload = await apiJson<SessionPayload>(`/api/history/${item.id}`);
      setSession(payload);
      setActiveMemberName(payload.summary.members[0]?.name ?? null);
      setSelectedAnalysisId(payload.analyses[payload.analyses.length - 1]?.id ?? null);
      await loadScanTasks(payload.sessionId);
      await loadGitSyncJobs(payload.sessionId);
      navigateTo('/workspace');

      setGitAction('sync');
      const syncPayload = await apiJson<{ job: GitSyncJob }>(`/api/session/${payload.sessionId}/git-sync`, {
        method: 'POST'
      });
      setGitSyncJobs(previous => {
        const rest = previous.filter(job => job.id !== syncPayload.job.id);
        return [syncPayload.job, ...rest].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
      });
      await refreshGitSyncJob(payload.sessionId, syncPayload.job.id);
      setGitInfo(syncPayload.job.status === 'completed' ? 'Git sync finished.' : 'Git sync queued.');
    } catch (syncError) {
      setGitError(syncError instanceof Error ? syncError.message : 'Failed to start Git sync.');
    } finally {
      setGitAction(null);
    }
  }

  async function handleAnalyzeMember(memberName = activeMember?.name ?? null) {
    if (!session || !memberName) {
      setAnalysisError('No member selected.');
      return;
    }

    setAnalysisError(null);
    setAnalysisAction('member');
    setAnalysisTargetMemberName(memberName);

    try {
      const payload = await apiJson<{ analysis: LlmAnalysisResult; analyses: LlmAnalysisResult[] }>(`/api/session/${session.sessionId}/analyze`, {
        method: 'POST',
        body: JSON.stringify({
          scope: 'member',
          memberName
        })
      });

      setSession(previous => previous ? { ...previous, analyses: payload.analyses } : previous);
      setSelectedAnalysisId(payload.analysis.id);
      setActiveMemberName(memberName);
      setWorkspacePane('members');
      await loadHistory();
    } catch (analysisFailure) {
      setAnalysisError(analysisFailure instanceof Error ? analysisFailure.message : 'Analysis failed.');
    } finally {
      setAnalysisAction(null);
      setAnalysisTargetMemberName(null);
    }
  }

  async function handleScanDataset() {
    if (!session) {
      return;
    }

    setAnalysisError(null);
    setAnalysisAction('scan');

    try {
      const payload = await apiJson<{ task: DatasetScanTask }>(`/api/session/${session.sessionId}/scan`, {
        method: 'POST',
        body: JSON.stringify({
          scope: 'dataset'
        })
      });
      setScanTasks(previous => {
        const rest = previous.filter(task => task.id !== payload.task.id);
        return [payload.task, ...rest].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
      });
      await refreshScanTask(session.sessionId, payload.task.id);
    } catch (analysisFailure) {
      setAnalysisError(analysisFailure instanceof Error ? analysisFailure.message : 'Dataset scan failed.');
    } finally {
      setAnalysisAction(null);
    }
  }

  async function handleDownload() {
    if (!session) {
      return;
    }
    const response = await fetch(`/api/session/${session.sessionId}/download.zip`, {
      credentials: 'include'
    });
    if (!response.ok) {
      const payload = await parseApiResponse(response);
      setError(typeof payload.error === 'string' ? payload.error : 'Download failed.');
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${session.summary.datasetName}.zip`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }

  async function handleDownloadXmi() {
    if (!session) {
      return;
    }
    const response = await fetch(`/api/session/${session.sessionId}/download.xmi`, {
      credentials: 'include'
    });
    if (!response.ok) {
      const payload = await parseApiResponse(response);
      setError(typeof payload.error === 'string' ? payload.error : 'XMI export failed.');
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${session.summary.datasetName}.xmi`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }

  async function handleReparse() {
    if (!session) {
      return;
    }

    setError(null);
    setIsReparsing(true);

    try {
      const payload = await apiJson<{ session: SessionPayload }>(`/api/session/${session.sessionId}/reparse`, {
        method: 'POST',
        body: JSON.stringify({
          encoding: session.summary.requestedEncoding || encoding
        })
      });
      setSession(payload.session);
      setActiveMemberName(payload.session.summary.members[0]?.name ?? null);
      setSelectedAnalysisId(payload.session.analyses[payload.session.analyses.length - 1]?.id ?? null);
      await loadHistory();
    } catch (reparseError) {
      setError(reparseError instanceof Error ? reparseError.message : 'Reparse failed.');
    } finally {
      setIsReparsing(false);
    }
  }

  function openSelectedAnalysis(analysisId?: string) {
    const nextAnalysis = analysisId
      ? analyses.find(analysis => analysis.id === analysisId) ?? null
      : selectedAnalysis;

    if (!nextAnalysis) {
      return;
    }
    setSelectedAnalysisId(nextAnalysis.id);
    if (nextAnalysis.memberName) {
      setActiveMemberName(nextAnalysis.memberName);
      setWorkspacePane('members');
    }
    setIsAnalysisModalOpen(true);
  }

  if (publicPageKey) {
    return <PublicPage pageKey={publicPageKey} />;
  }

  if (isAuthLoading) {
    return (
      <div className="auth-shell">
        <InlineLoading description="Checking session" />
      </div>
    );
  }

  if (!user) {
    return (
      <AuthPanel
        mode={authMode}
        email={authEmail}
        password={authPassword}
        code={authCode}
        devCode={devCode}
        error={authError}
        info={authInfo}
        isSubmitting={isAuthSubmitting}
        onModeChange={nextMode => {
          setAuthMode(nextMode);
          setAuthError(null);
          setAuthInfo(null);
          setDevCode(null);
        }}
        onEmailChange={setAuthEmail}
        onPasswordChange={setAuthPassword}
        onCodeChange={setAuthCode}
        onLogin={() => void handleLogin()}
        onRequestCode={() => void handleRequestCode()}
        onRegister={() => void handleRegister()}
      />
    );
  }

  if (isAdminPage) {
    if (!user.isAdmin) {
      return (
        <div className="auth-shell">
          <Tile className="auth-panel">
            <Stack gap={5}>
              <div>
                <p className="eyebrow">Admin</p>
                <h1>Administrator access required</h1>
                <p className="auth-support">Sign in with the configured administrator account to open this page.</p>
              </div>
              <Button href="/">Back to workspace</Button>
            </Stack>
          </Tile>
        </div>
      );
    }

    return (
      <div className="app-shell admin-page-shell">
        <header className="app-topbar admin-page-topbar">
          <div className="topbar-main">
            <div>
              <p className="eyebrow">Admin</p>
              <h1>Management console</h1>
            </div>
          </div>
          <div className="user-strip">
            {buildInfo ? (
              <span
                className="build-pill"
                title={`Build time: ${formatBuildTime(buildInfo.buildTime)}. Environment: ${buildInfo.environment}.`}
              >
                v{buildInfo.version} · {formatCommit(buildInfo.commit)}
              </span>
            ) : null}
            <Button kind="ghost" size="sm" href="/">
              Workspace
            </Button>
            <span className="user-strip__email">{user.email}</span>
            <Button kind="ghost" size="sm" renderIcon={Logout} onClick={() => void handleLogout()}>
              Logout
            </Button>
          </div>
        </header>

        <Content className="admin-page-content">
          <AdminManagementPanel
            activeView={adminPanelView}
            data={adminOperations}
            error={adminOperationsError}
            info={adminInfo}
            isLoading={isAdminOperationsLoading}
            isSavingPrompt={isSavingAdminPrompt}
            promptProviderId={adminPromptProviderId}
            promptBaseUrl={adminPromptBaseUrl}
            promptModel={adminPromptModel}
            promptScope={adminPromptScope}
            promptText={adminPromptText}
            onViewChange={setAdminPanelView}
            onRefresh={() => void loadAdminOperations()}
            onErrorClose={() => setAdminOperationsError(null)}
            onInfoClose={() => setAdminInfo(null)}
            onPromptProviderIdChange={setAdminPromptProviderId}
            onPromptBaseUrlChange={setAdminPromptBaseUrl}
            onPromptModelChange={setAdminPromptModel}
            onPromptScopeChange={setAdminPromptScope}
            onPromptTextChange={setAdminPromptText}
            onPromptSave={() => void handleSaveAdminPrompt()}
          />
        </Content>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="app-topbar">
        <div className="topbar-main">
          <div>
            <p className="eyebrow">XMI Workbench</p>
            <h1>Review extracted members</h1>
          </div>
        </div>
        <div className="user-strip">
          {buildInfo ? (
            <span
              className="build-pill"
              title={`Build time: ${formatBuildTime(buildInfo.buildTime)}. Environment: ${buildInfo.environment}.`}
            >
              v{buildInfo.version} · {formatCommit(buildInfo.commit)}
            </span>
          ) : null}
          <Button
            kind="secondary"
            size="sm"
            renderIcon={Upload}
            onClick={() => {
              setError(null);
              setIsUploadModalOpen(true);
            }}
          >
            Upload dataset
          </Button>
          <Button
            kind={activeWorkspacePage === 'workspace' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => navigateTo('/workspace')}
          >
            Workspace
          </Button>
          <Button
            kind={activeWorkspacePage === 'datasets' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => navigateTo('/datasets')}
          >
            Datasets
          </Button>
          <Button kind="tertiary" size="sm" renderIcon={Help} className="topbar-guide-button" href="/guide">
            Guide
          </Button>
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={LogoGithub}
            iconDescription="Personal Git settings"
            tooltipPosition="bottom"
            onClick={() => {
              setGitError(null);
              setGitInfo(null);
              setIsPersonalSettingsOpen(true);
            }}
          />
          {user.isAdmin ? (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Dashboard}
              iconDescription="Admin management"
              tooltipPosition="bottom"
              href="/admin"
            />
          ) : null}
          <span className="user-strip__email">{user.email}</span>
          <Button kind="ghost" size="sm" renderIcon={Logout} onClick={() => void handleLogout()}>
            Logout
          </Button>
        </div>
      </header>

      <Content className="workspace">
        {activeWorkspacePage === 'datasets' ? (
          <section className="datasets-page">
            <div className="datasets-page__hero">
              <div>
                <p className="panel-label">Datasets</p>
                <h2>Manage uploaded datasets</h2>
                <p className="workspace-helper">Open, edit, delete, reparse, and sync datasets to Git from one page.</p>
              </div>
              <div className="datasets-page__hero-actions">
                {isHistoryLoading ? <InlineLoading description="Loading datasets" /> : null}
                <Button
                  kind="secondary"
                  size="sm"
                  renderIcon={Upload}
                  onClick={() => {
                    setError(null);
                    setIsUploadModalOpen(true);
                  }}
                >
                  Upload dataset
                </Button>
              </div>
            </div>

            <div className="datasets-summary-grid">
              <Tile className="datasets-summary-card">
                <span className="datasets-summary-card__label">datasets</span>
                <strong>{groupedHistoryItems.length}</strong>
                <span>{historyItems.length} uploads tracked</span>
              </Tile>
              <Tile className="datasets-summary-card">
                <span className="datasets-summary-card__label">retained source</span>
                <strong>{retainedHistoryCount}</strong>
                <span>ready for reparse</span>
              </Tile>
              <Tile className="datasets-summary-card">
                <span className="datasets-summary-card__label">saved AI outputs</span>
                <strong>{totalSavedHistoryAnalyses}</strong>
                <span>attached to upload history</span>
              </Tile>
              <Tile className="datasets-summary-card">
                <span className="datasets-summary-card__label">Git repository</span>
                <strong>{isGitConfigured ? `${gitRepositoryOwner}/${gitRepositoryName}` : 'Not configured'}</strong>
                <span>{isGitConfigured ? 'ready to sync' : 'open Git settings first'}</span>
              </Tile>
            </div>

            <Tile className="panel datasets-panel">
              <div className="panel-head panel-head--stack datasets-panel__head">
                <div>
                  <p className="panel-label">Library</p>
                  <div className="datasets-panel__title-row">
                    <h3>Your uploads</h3>
                    <details className="dataset-help">
                      <summary aria-label="Dataset metadata help">
                        <Help size={16} />
                      </summary>
                      <div className="dataset-help__body">
                        <p><strong>CP037 / CP930</strong> is the code page used to decode member text. CP037 is common English EBCDIC; CP930 is Japanese EBCDIC.</p>
                        <p><strong>records</strong> counts NETDATA records in XMI/XMIT archives or lines in TXT imports. <strong>data records</strong> counts content records after archive control metadata.</p>
                        <p><strong>members</strong> are extracted PDS members. TXT uploads are shown as simulated members so they can be searched, edited, analyzed, and synced the same way.</p>
                      </div>
                    </details>
                  </div>
                </div>
                <div className="datasets-panel__head-actions">
                  <TextInput
                    id="history-search"
                    labelText="Search history"
                    placeholder="Search dataset or file name"
                    value={historySearchQuery}
                    onChange={event => setHistorySearchQuery(event.target.value)}
                  />
                </div>
              </div>

              <div className="history-bulk-bar">
                <div>
                  <strong>{selectedHistoryIds.length}</strong>
                  <span> selected</span>
                  {selectedHistoryIds.length > 0 ? <span> · {selectableReparseCount} retained source</span> : null}
                </div>
                <div className="history-bulk-bar__actions">
                  <Button kind="ghost" size="sm" onClick={selectVisibleHistoryItems} disabled={filteredHistoryIds.length === 0 || selectedVisibleHistoryIds.length === filteredHistoryIds.length || isBulkHistoryBusy}>
                    Select visible
                  </Button>
                  <Button kind="ghost" size="sm" onClick={clearHistorySelection} disabled={selectedHistoryIds.length === 0 || isBulkHistoryBusy}>
                    Clear
                  </Button>
                  <Button kind="secondary" size="sm" onClick={() => void handleBulkReparseHistory()} disabled={selectableReparseCount === 0 || isBulkHistoryBusy}>
                    {bulkHistoryAction === 'reparse' ? 'Reparsing...' : 'Reparse selected'}
                  </Button>
                  <Button kind="danger--ghost" size="sm" onClick={requestBulkDeleteHistory} disabled={selectedHistoryIds.length === 0 || isBulkHistoryBusy}>
                    Delete selected
                  </Button>
                </div>
              </div>

              {historyError ? (
                <InlineNotification
                  lowContrast
                  kind="error"
                  title="History failed"
                  subtitle={historyError}
                  onCloseButtonClick={() => setHistoryError(null)}
                />
              ) : null}

              {historyInfo ? (
                <InlineNotification
                  lowContrast
                  kind="success"
                  title="History updated"
                  subtitle={historyInfo}
                  onCloseButtonClick={() => setHistoryInfo(null)}
                />
              ) : null}

              {groupedHistoryItems.length === 0 ? (
                <div className="history-empty">
                  <Document size={28} />
                  <p>{historyItems.length === 0 ? 'No saved uploads yet.' : 'No history matches.'}</p>
                </div>
              ) : (
                <div className="datasets-groups">
                  {groupedHistoryItems.map(group => (
                    <section className="dataset-group-card" key={group.key}>
                      <div className="dataset-group-card__header">
                        <div>
                          <p className="panel-label">Dataset</p>
                          <h4>{group.datasetName}</h4>
                        </div>
                        <div className="dataset-group-card__stats">
                          <span>{group.items.length} uploads</span>
                          <span>{group.totalMembers} members</span>
                          <span>{group.totalAnalyses} AI outputs</span>
                          <span>{formatDateTime(group.latestUploadedAt)}</span>
                        </div>
                      </div>
                      <div className="dataset-group-card__items">
                        {group.items.map(item => {
                          const isActive = session?.uploadId === item.id;
                          const isRowBusy = isOpeningHistoryId === item.id || deletingHistoryId === item.id || isBulkHistoryBusy;
                          return (
                            <article
                              className={`history-card history-card--management${isActive ? ' history-card--active' : ''}${isRowBusy ? ' history-card--busy' : ''}`}
                              key={item.id}
                              role="button"
                              tabIndex={isRowBusy ? -1 : 0}
                              aria-label={`Open ${item.fileName}`}
                              onClick={() => {
                                if (!isRowBusy) {
                                  void handleOpenHistory(item.id);
                                }
                              }}
                              onKeyDown={event => {
                                if (isRowBusy || (event.key !== 'Enter' && event.key !== ' ')) {
                                  return;
                                }
                                event.preventDefault();
                                void handleOpenHistory(item.id);
                              }}
                            >
                              <label
                                className="history-card__select"
                                aria-label={`Select ${item.fileName}`}
                                onClick={event => event.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedHistoryIds.includes(item.id)}
                                  onChange={event => toggleHistorySelection(item.id, event.target.checked)}
                                  disabled={isBulkHistoryBusy || deletingHistoryId === item.id}
                                />
                              </label>
                              <div className="history-card__content">
                                <div className="history-card__head">
                                  <strong>{item.fileName}</strong>
                                  <div className="history-card__tags">
                                    {isActive ? <Tag type="blue">Open</Tag> : null}
                                    {item.originalStored ? <Tag type="cyan">source saved</Tag> : <Tag type="cool-gray">no source</Tag>}
                                  </div>
                                </div>
                                <div className="history-card__meta">
                                  <span>{item.memberCount} members</span>
                                  <span>{item.analysisCount} saved analyses</span>
                                  <span>{item.selectedEncoding}</span>
                                </div>
                                <div className="history-card__meta">
                                  <span>{formatDateTime(item.uploadedAt)}</span>
                                  <span>{item.records} records</span>
                                  <span>{item.dataRecords} data records</span>
                                </div>
                              </div>
                              <div className="history-card__actions" onClick={event => event.stopPropagation()}>
                                <Button
                                  kind="ghost"
                                  size="sm"
                                  onClick={() => void handleManageDataset(item)}
                                  disabled={isRowBusy}
                                >
                                  Edit members
                                </Button>
                                <Button
                                  kind="ghost"
                                  size="sm"
                                  onClick={() => void handleReparseHistoryItem(item)}
                                  disabled={!item.originalStored || isRowBusy}
                                >
                                  {bulkHistoryAction === 'reparse' ? 'Reparsing...' : 'Reparse'}
                                </Button>
                                <Button
                                  kind="secondary"
                                  size="sm"
                                  onClick={() => void handleSyncHistoryItemToGit(item)}
                                  disabled={!isGitConfigured || gitAction !== null || Boolean(activeGitSyncJob) || isRowBusy}
                                >
                                  Sync Git
                                </Button>
                                <Button
                                  kind="danger--ghost"
                                  size="sm"
                                  renderIcon={TrashCan}
                                  iconDescription={`Delete ${item.fileName}`}
                                  className="history-card__delete"
                                  onClick={() => requestDeleteHistory(item)}
                                  disabled={isRowBusy}
                                >
                                  Delete
                                </Button>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </Tile>
          </section>
        ) : (
          <Tile className="panel panel-browser panel-browser--expanded">
              <div className="workspace-header">
                <div className="workspace-header__title">
                  <p className="panel-label">Workspace</p>
                  <h2>{session?.summary.datasetName ?? 'Awaiting archive'}</h2>
                  {session ? (
                    <p className="workspace-subtitle">{session.fileName}</p>
                  ) : null}
                </div>
                <div className="workspace-status">
                  <Button kind="ghost" size="sm" onClick={() => navigateTo('/datasets')}>
                    Manage datasets
                  </Button>
                  {session ? <Tag type="blue">{members.length} members</Tag> : null}
                  {session?.originalStored ? <Tag type="cyan">source saved</Tag> : null}
                  {analyses.length > 0 ? <Tag type="green">{analyses.length} saved outputs</Tag> : null}
                  {activeScanTask ? <Tag type="warm-gray">{activeScanTask.status}</Tag> : null}
                  {isUploading ? <InlineLoading description="Parsing" /> : null}
                  {isReparsing ? <InlineLoading description="Reparsing" /> : null}
                </div>
              </div>

              {error && !isUploadModalOpen ? (
                <InlineNotification
                  lowContrast
                  kind="error"
                  title="Action failed"
                  subtitle={error}
                  onCloseButtonClick={() => setError(null)}
                />
              ) : null}

              {duplicateUploadNotice ? (
                <InlineNotification
                  lowContrast
                  kind="warning"
                  title="Duplicate upload detected"
                  subtitle={`This file content was already uploaded as ${duplicateUploadNotice.fileName} on ${formatDateTime(duplicateUploadNotice.uploadedAt)}.`}
                  onCloseButtonClick={() => setDuplicateUploadNotice(null)}
                />
              ) : null}

              {!session ? (
                <div className="empty-state">
                  <Upload size={32} />
                  <h3>Upload an archive to start</h3>
                  <p>Detailed member analysis and dataset scan results will be saved into your private history automatically.</p>
                  <div className="empty-state__actions">
                    <Button
                      renderIcon={Upload}
                      onClick={() => {
                        setError(null);
                        setIsUploadModalOpen(true);
                      }}
                    >
                      Open upload dialog
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="workspace-page">
                  <div className="workspace-controls">
                    <div className="workspace-controls__search">
                      <TextInput
                        id="member-search"
                        labelText=""
                        hideLabel
                        placeholder="Search member name"
                        value={memberSearchQuery}
                        onChange={event => setMemberSearchQuery(event.target.value)}
                      />
                      <label className="workspace-controls__search-scope" htmlFor="member-search-content">
                        <input
                          id="member-search-content"
                          type="checkbox"
                          checked={searchMemberContent}
                          onChange={event => setSearchMemberContent(event.target.checked)}
                        />
                        <span>Search content</span>
                      </label>
                      <div className="workspace-controls__search-tools">
                        <span>
                          {isMemberSearchPending
                            ? 'Searching...'
                            : normalizedImmediateMemberSearch
                              ? `${visibleMembers.length}/${members.length} matched`
                              : `${members.length} members`}
                        </span>
                        <Button
                          kind="ghost"
                          size="sm"
                          onClick={saveCurrentMemberFilter}
                          disabled={!canSaveMemberFilter}
                        >
                          Save filter
                        </Button>
                      </div>
                      {savedMemberFilters.length > 0 ? (
                        <div className="saved-member-filters" aria-label="Saved member filters">
                          {savedMemberFilters.map(filter => (
                            <span className="saved-member-filter" key={filter.id}>
                              <button
                                type="button"
                                onClick={() => applyMemberFilter(filter)}
                                title={`${filter.query}${filter.searchContent ? ' in names and content' : ' in names'}`}
                              >
                                {filter.label}
                                <small>{filter.searchContent ? 'content' : 'name'}</small>
                              </button>
                              <button
                                type="button"
                                aria-label={`Remove saved filter ${filter.label}`}
                                onClick={() => removeSavedMemberFilter(filter.id)}
                              >
                                x
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <div className="workspace-controls__actions">
                      <Button
                        kind="secondary"
                        renderIcon={Watson}
                        onClick={() => void handleAnalyzeMember()}
                        disabled={!session || !activeMember || analysisAction !== null}
                      >
                        {analysisAction === 'member' && analysisTargetMemberName === activeMember?.name ? 'Analyzing...' : 'Analyze'}
                      </Button>
                      <Button
                        kind="tertiary"
                        renderIcon={CodeBlock}
                        onClick={() => openSelectedAnalysis()}
                        disabled={!selectedAnalysis}
                      >
                        Output
                      </Button>
                      <Button
                        kind="ghost"
                        renderIcon={Watson}
                        onClick={() => void handleScanDataset()}
                        disabled={!session || analysisAction !== null || Boolean(activeScanTask)}
                        title="Runs a controlled dataset scan with capped member summaries and truncated source excerpts."
                      >
                        {analysisAction === 'scan'
                          ? 'Queuing...'
                          : activeScanTask
                            ? 'Scanning'
                            : 'Safe scan'}
                      </Button>
                    </div>

                    <div className="workspace-controls__utility">
                      <Button
                        kind="ghost"
                        hasIconOnly
                        renderIcon={Copy}
                        iconDescription={copyState === 'done' ? 'Copied' : 'Copy member'}
                        tooltipPosition="bottom"
                        onClick={() => void handleCopy()}
                        disabled={!activeMember}
                      />
                      <Button
                        kind="ghost"
                        hasIconOnly
                        renderIcon={Document}
                        iconDescription={session.originalStored ? 'Reparse original XMI' : 'Original XMI not stored'}
                        tooltipPosition="bottom"
                        onClick={() => void handleReparse()}
                        disabled={!session.originalStored || isReparsing}
                      />
                      <Button
                        kind="ghost"
                        hasIconOnly
                        renderIcon={Download}
                        iconDescription="Download ZIP and summary"
                        tooltipPosition="bottom"
                        onClick={() => void handleDownload()}
                        disabled={!session}
                      />
                      <Button
                        kind="ghost"
                        hasIconOnly
                        renderIcon={CodeBlock}
                        iconDescription="Export simulated PDS as XMI"
                        tooltipPosition="bottom"
                        onClick={() => void handleDownloadXmi()}
                        disabled={!session || members.length === 0}
                      />
                      <Button
                        kind="ghost"
                        hasIconOnly
                        renderIcon={Upload}
                        iconDescription="Upload another archive"
                        tooltipPosition="bottom"
                        onClick={() => {
                          setError(null);
                          setIsUploadModalOpen(true);
                        }}
                      />
                    </div>
                  </div>

                  {normalizedMemberSearch ? (
                    <div className="workspace-meta-bar">
                      <span>
                        {visibleMembers.length} match{visibleMembers.length === 1 ? '' : 'es'}
                      </span>
                      {searchMemberContent && activeMemberMatchLines.length > 0 ? (
                        <div className="workspace-meta-bar__actions">
                          <span>line {activeMatchIndex + 1}/{activeMemberMatchLines.length}</span>
                          <Button
                            kind="ghost"
                            size="sm"
                            disabled={activeMemberMatchLines.length <= 1}
                            onClick={() => setActiveMatchIndex(previous => previous <= 0 ? activeMemberMatchLines.length - 1 : previous - 1)}
                          >
                            Prev
                          </Button>
                          <Button
                            kind="ghost"
                            size="sm"
                            disabled={activeMemberMatchLines.length <= 1}
                            onClick={() => setActiveMatchIndex(previous => previous >= activeMemberMatchLines.length - 1 ? 0 : previous + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {analysisError ? (
                    <InlineNotification
                      lowContrast
                      kind="error"
                      title="Analysis failed"
                      subtitle={analysisError}
                      onCloseButtonClick={() => setAnalysisError(null)}
                    />
                  ) : null}

                  {latestScanTask?.status === 'failed' ? (
                    <InlineNotification
                      lowContrast
                      kind="error"
                      title={formatScanTaskTitle(latestScanTask)}
                      subtitle={formatScanTaskSubtitle(latestScanTask)}
                      hideCloseButton
                    />
                  ) : null}

                  <div className="workspace-content">
                    <aside className="workspace-sidebar">
                      <div className="workspace-tabs">
                        <button
                          type="button"
                          className={`workspace-tab${workspacePane === 'members' ? ' workspace-tab--active' : ''}`}
                          aria-pressed={workspacePane === 'members'}
                          onClick={() => setWorkspacePane('members')}
                        >
                          Members
                          <span>{visibleMembers.length}</span>
                        </button>
                        <button
                          type="button"
                          className={`workspace-tab${workspacePane === 'analyses' ? ' workspace-tab--active' : ''}`}
                          aria-pressed={workspacePane === 'analyses'}
                          onClick={() => setWorkspacePane('analyses')}
                        >
                          Saved summaries
                          <span>{analyses.length}</span>
                        </button>
                      </div>

                      {workspacePane === 'members' ? (
                        <div className="member-terminal">
                          <div className="member-terminal__header">
                            <span>member list</span>
                            <span>{visibleMembers.length}</span>
                          </div>
                          {visibleMembers.length === 0 ? (
                            <div className="member-list__empty">
                              <p>{normalizedMemberSearch ? 'No member names or content matched.' : 'No extracted members.'}</p>
                            </div>
                          ) : searchableMembers.map(result => (
                            <div
                              key={result.member.name}
                              className={`member-list__item${activeMember?.name === result.member.name ? ' member-list__item--active' : ''}`}
                            >
                              <button
                                type="button"
                                className="member-list__select"
                                onClick={() => {
                                  setActiveMemberName(result.member.name);
                                  setWorkspacePane('members');
                                }}
                              >
                                <span className="member-list__prompt">{activeMember?.name === result.member.name ? '>' : ' '}</span>
                                <span className="member-list__name">{result.member.name}</span>
                                <span className="member-list__meta">{result.member.language.toUpperCase()} · {result.member.recordCount}</span>
                                {result.nameMatched || result.contentMatched ? (
                                  <span className="member-list__match">
                                    {result.nameMatched ? 'name' : ''}{result.nameMatched && result.contentMatched ? ' / ' : ''}{result.contentMatched ? 'content' : ''}
                                  </span>
                                ) : null}
                              </button>
                              <Button
                                kind={latestSavedAnalysisByMember.has(result.member.name) ? 'tertiary' : 'ghost'}
                                size="sm"
                                hasIconOnly
                                renderIcon={Watson}
                                iconDescription={latestSavedAnalysisByMember.has(result.member.name) ? `Open saved AI for ${result.member.name}` : `Run AI for ${result.member.name}`}
                                tooltipPosition="right"
                                className="member-list__ai"
                                onClick={() => {
                                  const saved = latestSavedAnalysisByMember.get(result.member.name);
                                  setActiveMemberName(result.member.name);
                                  if (saved) {
                                    openSelectedAnalysis(saved.id);
                                  } else {
                                    void handleAnalyzeMember(result.member.name);
                                  }
                                }}
                                disabled={analysisAction === 'member' && analysisTargetMemberName === result.member.name}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="analysis-log">
                          <div className="analysis-log__head">
                            <div>
                              <p className="panel-label">Saved outputs</p>
                              <h3>Current dataset analysis history</h3>
                            </div>
                            {latestAnalysis ? <Tag type="green">Latest {formatAnalysisKind(latestAnalysis.kind)}</Tag> : null}
                          </div>
                          {analyses.length === 0 ? (
                            <p className="analysis-empty">No saved outputs yet. Run a member analysis or a dataset scan.</p>
                          ) : (
                            <div className="analysis-log__sections">
                              <section className="analysis-log__section">
                                <div className="analysis-log__section-head">
                                  <p className="panel-label">Dataset</p>
                                  {datasetOverviewAnalysis ? <Tag type="green">1 saved</Tag> : null}
                                </div>
                                {datasetOverviewAnalysis ? (
                                  <button
                                    type="button"
                                    className={`analysis-log__item${selectedAnalysis?.id === datasetOverviewAnalysis.id ? ' analysis-log__item--active' : ''}`}
                                    onClick={() => openSelectedAnalysis(datasetOverviewAnalysis.id)}
                                  >
                                    <div className="analysis-log__item-title">
                                      <strong>{formatAnalysisTitle(datasetOverviewAnalysis)}</strong>
                                      <Tag type="green">{formatAnalysisKind(datasetOverviewAnalysis.kind)}</Tag>
                                    </div>
                                    <div className="analysis-log__item-meta">
                                      <span>{formatDateTime(datasetOverviewAnalysis.createdAt)}</span>
                                      <span>background scan</span>
                                    </div>
                                  </button>
                                ) : (
                                  <p className="analysis-empty">No dataset overview saved yet.</p>
                                )}
                              </section>

                              <section className="analysis-log__section">
                                <div className="analysis-log__section-head">
                                  <p className="panel-label">Members</p>
                                  <Tag type="cool-gray">{memberAnalysisGroups.length}</Tag>
                                </div>
                                {memberAnalysisGroups.length === 0 ? (
                                  <p className="analysis-empty">No member summaries saved yet.</p>
                                ) : (
                                  <div className="analysis-log__list">
                                    {memberAnalysisGroups.map(group => {
                                      const preferredAnalysis = group.latestSummary ?? group.latestDetail ?? group.latest;
                                      return (
                                        <button
                                          key={group.memberName}
                                          type="button"
                                          className={`analysis-log__item${selectedAnalysis?.id === preferredAnalysis.id ? ' analysis-log__item--active' : ''}`}
                                          onClick={() => openSelectedAnalysis(preferredAnalysis.id)}
                                        >
                                          <div className="analysis-log__item-title">
                                            <strong>{group.memberName}</strong>
                                            <Tag type={preferredAnalysis.kind === 'member_summary' ? 'cool-gray' : 'blue'}>
                                              {formatAnalysisKind(preferredAnalysis.kind)}
                                            </Tag>
                                          </div>
                                          <div className="analysis-log__item-meta">
                                            <span>{formatDateTime(preferredAnalysis.createdAt)}</span>
                                            <span>{group.total} saved output{group.total === 1 ? '' : 's'}</span>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </section>
                            </div>
                          )}
                        </div>
                      )}
                    </aside>

                    <section className="workspace-main">
                      <div className="preview-shell">
                        <div className="preview-shell__bar">
                          <span>{session.summary.datasetName}</span>
                          <span>{session.summary.selectedEncoding}</span>
                          <span>{activeMember ? activeMember.language.toUpperCase() : 'idle'}</span>
                        </div>
                        <div className="preview-head">
                          <div className="preview-head__copy">
                            <h3>{activeMember?.name ?? 'No member selected'}</h3>
                            <p>
                              {activeMember
                                ? `${activeMember.language.toUpperCase()} • ${activeMember.recordCount} records`
                                : 'Choose a member from the left list to preview its content.'}
                            </p>
                          </div>
                          <div className="preview-meta">
                            {activeMemberSavedAnalysis ? (
                              <Button
                                kind="tertiary"
                                size="sm"
                                renderIcon={Watson}
                                onClick={() => openSelectedAnalysis(activeMemberSavedAnalysis.id)}
                              >
                                {activeMemberSavedAnalysis.kind === 'member_summary' ? 'Open saved AI summary' : 'Open saved AI output'}
                              </Button>
                            ) : null}
                            {activeMember && !isEditingMember ? (
                              <Button
                                kind="ghost"
                                size="sm"
                                renderIcon={Edit}
                                onClick={handleStartMemberEdit}
                              >
                                Edit
                              </Button>
                            ) : null}
                            <span className="preview-meta__encoding">{session.summary.selectedEncoding}</span>
                          </div>
                        </div>

                        {activeMember && isEditingMember ? (
                          <div className="member-editor-shell">
                            <div className="member-editor-shell__header">
                              <span>{activeMember.name}</span>
                              <span>{memberEditorLineCount} lines</span>
                              {isMemberEditDirty ? <span>modified</span> : <span>clean</span>}
                            </div>
                            <textarea
                              className="member-editor"
                              spellCheck={false}
                              value={memberEditText}
                              onChange={event => setMemberEditText(event.target.value)}
                            />
                            <div className="member-editor-actions">
                              {memberSaveError ? <span className="member-editor-actions__error">{memberSaveError}</span> : <span />}
                              <div>
                                <Button
                                  kind="ghost"
                                  size="sm"
                                  renderIcon={Close}
                                  onClick={handleCancelMemberEdit}
                                  disabled={isSavingMember}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  kind="primary"
                                  size="sm"
                                  renderIcon={Save}
                                  onClick={() => void handleSaveMemberEdit()}
                                  disabled={!isMemberEditDirty || isSavingMember}
                                >
                                  {isSavingMember ? 'Saving...' : 'Save'}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : activeMember ? (
                          <MemberPreview
                            member={activeMember}
                            searchQuery={normalizedMemberSearch}
                            highlightLineNumbers={activeMemberMatchLines}
                            activeHighlightLineNumber={activeMatchLineNumber}
                          />
                        ) : (
                          <div className="no-members-state">
                            <Document size={32} />
                            <h3>{members.length === 0 ? 'No members extracted' : normalizedMemberSearch ? 'Select a matched member' : 'No member selected'}</h3>
                            <p>
                              {members.length === 0
                                ? 'Parsing has finished, but the file did not expose any fixed-length text PDS members that this parser can safely preview.'
                                : normalizedMemberSearch
                                  ? 'Search results are listed in the left navigation. Choose one member to inspect it.'
                                  : 'Choose a member from the left navigation to preview its content.'}
                            </p>
                            {members.length === 0 ? (
                              <div className="parse-stats">
                                <span>{session.summary.records} NETDATA records</span>
                                <span>{session.summary.dataRecords} data records</span>
                                <span>{session.summary.selectedEncoding}</span>
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>

                      <details
                        className="git-sync-drawer"
                        open={isGitToolsOpen}
                        onToggle={event => setIsGitToolsOpen(event.currentTarget.open)}
                      >
                        <summary className="git-sync-drawer__summary">
                          <span className="git-sync-drawer__title">
                            <LogoGithub size={18} />
                            Dataset Git sync
                          </span>
                          <span className="git-sync-drawer__meta">
                            <span>{isGitConfigured ? `${gitRepositoryOwner}/${gitRepositoryName}` : 'optional'}</span>
                            <Tag type={isGitConfigured ? 'green' : 'warm-gray'}>{isGitConfigured ? 'configured' : 'not configured'}</Tag>
                            {activeGitSyncJob ? <Tag type="blue">{activeGitSyncJob.status}</Tag> : null}
                          </span>
                        </summary>

                        <div className="git-sync-panel">
                          <div className="git-sync-panel__head">
                            <div>
                              <p className="panel-label">Git</p>
                              <h4>{isGitConfigured ? 'Sync current dataset' : 'Configure personal Git first'}</h4>
                            </div>
                            <p className="git-sync-panel__hint">
                              Optional export for users who want member text and AI outputs versioned in a personal GitHub repository.
                            </p>
                          </div>

                          <details className="git-sync-help" open={!isGitConfigured}>
                            <summary>First-time setup guide</summary>
                            <ol>
                              <li>Create or choose a GitHub repository for extracted XMI members.</li>
                              <li>Open Git settings and connect this website's GitHub App.</li>
                              <li>In GitHub, authorize only the repositories this site should access. If Save is disabled, the selection is already saved.</li>
                              <li>Return here. If the site still says not installed, click Connect GitHub App again, then refresh repositories.</li>
                              <li>Select one repository here, confirm the branch, then save.</li>
                              <li>Use Sync dataset to Git for the current upload or a dataset opened from history.</li>
                            </ol>
                            <p>No personal access token is required. For an empty repository, the first sync creates the selected branch.</p>
                          </details>

                          <div className="git-sync-panel__actions">
                            <Button
                              kind="ghost"
                              size="sm"
                              renderIcon={LogoGithub}
                              onClick={() => setIsPersonalSettingsOpen(true)}
                              disabled={gitAction !== null}
                            >
                              Git settings
                            </Button>
                            <Button
                              kind="secondary"
                              size="sm"
                              onClick={() => void handleSyncToGit()}
                              disabled={!session || !isGitConfigured || gitAction !== null || Boolean(activeGitSyncJob)}
                            >
                              {gitAction === 'sync'
                                ? 'Starting...'
                                : activeGitSyncJob
                                  ? 'Syncing...'
                                  : 'Sync dataset to Git'}
                            </Button>
                            <Button
                              kind="tertiary"
                              size="sm"
                              onClick={() => void handleReadGitDatasets()}
                              disabled={gitAction !== null}
                            >
                              {gitAction === 'read' ? 'Reading...' : 'Read Git index'}
                            </Button>
                          </div>

                          {gitError ? (
                            <InlineNotification
                              lowContrast
                              kind="error"
                              title="Git sync failed"
                              subtitle={gitError}
                              onCloseButtonClick={() => setGitError(null)}
                            />
                          ) : null}

                          {gitInfo ? (
                            <InlineNotification
                              lowContrast
                              kind="success"
                              title="Git sync"
                              subtitle={gitInfo}
                              onCloseButtonClick={() => setGitInfo(null)}
                            />
                          ) : null}

                          {latestGitSyncJob ? (
                            <div className={`git-sync-panel__latest git-sync-panel__latest--${latestGitSyncJob.status}`}>
                              <div className="git-sync-panel__latest-main">
                                <Tag type={gitSyncStatusTag(latestGitSyncJob.status)}>{latestGitSyncJob.status}</Tag>
                                <span>{latestGitSyncJob.branch}</span>
                                <span>{latestGitSyncJob.syncedMemberCount} members</span>
                                <span>{formatDateTime(latestGitSyncJob.updatedAt)}</span>
                              </div>
                              {latestGitSyncJob.resultMessage ? <p>{latestGitSyncJob.resultMessage}</p> : null}
                              {latestGitSyncJob.errorMessage ? <p>{latestGitSyncJob.errorMessage}</p> : null}
                              <div className="git-sync-panel__latest-actions">
                                {latestGitSyncJob.commitHash ? (
                                  <Button
                                    kind="ghost"
                                    size="sm"
                                    href={buildGitCommitUrl(gitRepositoryHtmlUrl, latestGitSyncJob.commitHash) ?? undefined}
                                    target="_blank"
                                    rel="noreferrer"
                                    disabled={!buildGitCommitUrl(gitRepositoryHtmlUrl, latestGitSyncJob.commitHash)}
                                  >
                                    Commit {latestGitSyncJob.commitHash.slice(0, 10)}
                                  </Button>
                                ) : null}
                                {latestGitSyncJob.status === 'failed' ? (
                                  <Button
                                    kind="tertiary"
                                    size="sm"
                                    onClick={() => void handleRetryGitSync(latestGitSyncJob)}
                                    disabled={!session || !isGitConfigured || gitAction !== null || Boolean(activeGitSyncJob)}
                                  >
                                    {gitAction === 'retry' ? 'Retrying...' : 'Retry sync'}
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                          ) : null}

                          {latestFailedGitSyncJob && latestFailedGitSyncJob.id !== latestGitSyncJob?.id ? (
                            <div className="git-sync-panel__last-failure">
                              <div className="git-sync-panel__last-failure-head">
                                <Tag type="red">last failure</Tag>
                                <span>{formatDateTime(latestFailedGitSyncJob.updatedAt)}</span>
                              </div>
                              <p>{latestFailedGitSyncJob.errorMessage ?? 'Git sync failed.'}</p>
                            </div>
                          ) : null}

                          {gitRepositoryDatasets.length > 0 ? (
                            <div className="git-sync-index">
                              {gitRepositoryDatasets.map(dataset => (
                                <details className="git-sync-index__item" key={dataset.path}>
                                  <summary>
                                    <span>{dataset.datasetName}</span>
                                    <span>{dataset.memberCount} members · {dataset.analysisCount} AI outputs</span>
                                  </summary>
                                  <div className="git-sync-index__body">
                                    <div className="git-sync-index__meta">
                                      <span>{dataset.path}</span>
                                      <span>{dataset.selectedEncoding}</span>
                                      <span>{formatDateTime(dataset.generatedAt)}</span>
                                    </div>
                                    <div className="git-sync-index__commands">
                                      <Button
                                        kind="ghost"
                                        size="sm"
                                        onClick={() => void handleReadGitDatasetDetail(dataset.path)}
                                        disabled={gitAction !== null}
                                      >
                                        {gitAction === 'detail' && selectedGitDataset?.path !== dataset.path ? 'Reading...' : 'Open from Git'}
                                      </Button>
                                    </div>
                                    <div className="git-sync-index__members">
                                      {dataset.members.slice(0, 20).map(member => (
                                        <span key={member.filePath}>
                                          {member.name} · {member.language.toUpperCase()} · {member.aiOutputs.length} AI
                                        </span>
                                      ))}
                                      {dataset.members.length > 20 ? <span>+{dataset.members.length - 20} more</span> : null}
                                    </div>
                                  </div>
                                </details>
                              ))}
                            </div>
                          ) : null}

                          {selectedGitDataset ? (
                            <div className="git-sync-detail">
                              <div className="git-sync-detail__head">
                                <strong>{selectedGitDataset.datasetName}</strong>
                                <span>{selectedGitDataset.path}</span>
                              </div>
                              <div className="git-sync-detail__grid">
                                <section>
                                  <p className="panel-label">Members</p>
                                  {selectedGitDataset.memberContents.slice(0, 3).map(member => (
                                    <details className="git-sync-detail__file" key={member.filePath}>
                                      <summary>{member.name} · {member.language.toUpperCase()}</summary>
                                      <pre>{member.text}</pre>
                                    </details>
                                  ))}
                                </section>
                                <section>
                                  <p className="panel-label">AI outputs</p>
                                  {selectedGitDataset.aiContents.slice(0, 3).map(output => (
                                    <details className="git-sync-detail__file" key={output.filePath}>
                                      <summary>{output.memberName ?? 'dataset'} · {output.kind}</summary>
                                      <pre>{output.content}</pre>
                                    </details>
                                  ))}
                                  {selectedGitDataset.aiContents.length === 0 ? <p className="analysis-empty">No AI outputs in Git for this dataset.</p> : null}
                                </section>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </details>
                    </section>
                  </div>

                  {parserIssues.length > 0 ? (
                    <InlineNotification
                      lowContrast
                      kind="warning"
                      title="Parser issue detected"
                      subtitle={parserIssues[0]}
                      hideCloseButton
                    />
                  ) : null}
                </div>
              )}
          </Tile>
        )}
      </Content>

      <Modal
        open={pendingHistoryDeletion !== null}
        size="sm"
        danger
        modalLabel="History"
        modalHeading={pendingHistoryDeletion ? `Delete ${pendingHistoryDeletion.label}?` : 'Delete history item'}
        primaryButtonText={bulkHistoryAction === 'delete' || (pendingHistoryDeletion?.ids.some(id => deletingHistoryId === id)) ? 'Deleting...' : 'Delete'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={!pendingHistoryDeletion || Boolean(deletingHistoryId) || bulkHistoryAction === 'delete'}
        preventCloseOnClickOutside={Boolean(deletingHistoryId) || bulkHistoryAction === 'delete'}
        onRequestClose={closeDeleteHistoryModal}
        onRequestSubmit={() => {
          if (!pendingHistoryDeletion || deletingHistoryId || bulkHistoryAction === 'delete') {
            return;
          }
          if (pendingHistoryDeletion.mode === 'bulk') {
            void handleBulkDeleteHistory(pendingHistoryDeletion.ids);
          } else {
            void handleDeleteHistory(pendingHistoryDeletion.ids[0] ?? '');
          }
        }}
      >
        <p className="history-delete-modal__body">
          Saved AI outputs, retained source files, dataset scan tasks, Git sync jobs, and queued work for {pendingHistoryDeletion?.mode === 'bulk' ? 'these uploads' : 'this upload'} will also be deleted.
        </p>
      </Modal>

      <Modal
        open={isAnalysisModalOpen}
        passiveModal
        size="lg"
        modalLabel="Analysis"
        modalHeading={selectedAnalysis ? formatAnalysisTitle(selectedAnalysis) : 'No analysis yet'}
        onRequestClose={() => setIsAnalysisModalOpen(false)}
      >
        <div className="analysis-modal">
          {selectedAnalysis ? (
            <Stack gap={5}>
              <div className="analysis-report">
                <div className="analysis-report__hero">
                  <div>
                    <p className="panel-label">Saved output</p>
                    <h3>{formatAnalysisTitle(selectedAnalysis)}</h3>
                  </div>
                  <div className="analysis-modal__meta">
                    <Tag type="green">{formatAnalysisKind(selectedAnalysis.kind)}</Tag>
                    <Tag type="cool-gray">{formatDateTime(selectedAnalysis.createdAt)}</Tag>
                    {selectedAnalysis.memberName ? <Tag type="blue">{selectedAnalysis.memberName}</Tag> : null}
                  </div>
                </div>

                <div className="analysis-report__sections">
                  {formatAnalysisSections(selectedAnalysis.content).map((section, index) => (
                    <section className="analysis-section" key={`${section.title}-${index}`}>
                      <div className="analysis-section__title">
                        <span>{String(index + 1).padStart(2, '0')}</span>
                        <h4>{section.title}</h4>
                      </div>
                      <div className="analysis-section__body">
                        <AnalysisBody body={section.body} />
                      </div>
                    </section>
                  ))}
                </div>
              </div>
            </Stack>
          ) : (
            <p className="analysis-empty">Run member or dataset analysis from the workbench toolbar.</p>
          )}
        </div>
      </Modal>

      <Modal
        open={isPersonalSettingsOpen}
        passiveModal
        size="md"
        modalLabel="Personal settings"
        modalHeading="GitHub App repository"
        onRequestClose={() => setIsPersonalSettingsOpen(false)}
      >
        <div className="personal-settings-modal">
          <Stack gap={5}>
            <div className="git-settings-status">
              {isGitHubAppConfigured ? <Tag type="green">app configured</Tag> : <Tag type="red">server setup missing</Tag>}
              {gitHubInstallationId ? <Tag type="green">installed</Tag> : <Tag type="warm-gray">not installed</Tag>}
              {isGitConfigured ? <Tag type="green">{gitRepositoryOwner}/{gitRepositoryName}</Tag> : <Tag type="warm-gray">no repository</Tag>}
              <Tag type={gitAutoSync ? 'blue' : 'cool-gray'}>{gitAutoSync ? 'auto sync' : 'manual sync'}</Tag>
            </div>

            <details className="github-app-guide" open={!gitHubInstallationId}>
              <summary>First-time GitHub sync guide</summary>
              <ol>
                <li>Create or choose a GitHub repository for your extracted XMI members.</li>
                <li>Click Connect GitHub App. Normal users only authorize this website's GitHub App; you do not create your own app.</li>
                <li>In GitHub, choose Only select repositories and select the repository. If Save is disabled, the selection is already saved.</li>
                <li>Return here, click Connect GitHub App again if the site still says not installed, then Refresh repositories.</li>
                <li>Choose the repository here and click Save repository.</li>
                <li>Open or upload an XMI dataset and use Sync dataset to Git when you want to push member text and AI suggestions.</li>
              </ol>
              <p className="github-app-guide__note">
                No personal access token is required. Access is limited to repositories you explicitly authorize in GitHub.
              </p>
            </details>

            <div className="github-app-connect">
              <Button
                kind={gitHubInstallationId ? 'tertiary' : 'primary'}
                renderIcon={LogoGithub}
                onClick={() => void handleInstallGitHubApp()}
                disabled={!isGitHubAppConfigured || gitAction !== null}
              >
                {gitHubInstallationId ? 'Manage GitHub App' : 'Connect GitHub App'}
              </Button>
              <Button
                kind="secondary"
                onClick={() => void handleLoadGitHubRepositories()}
                disabled={!gitHubInstallationId || gitAction !== null}
              >
                {gitAction === 'repos' ? 'Loading...' : 'Refresh repositories'}
              </Button>
              {gitRepositoryHtmlUrl ? (
                <a className="github-app-connect__repo" href={gitRepositoryHtmlUrl} target="_blank" rel="noreferrer">
                  Open selected repository
                </a>
              ) : null}
              {!gitHubInstallationId ? (
                <span className="github-app-connect__hint">
                  If you already selected repositories in GitHub, click Connect GitHub App again to link that installation to this XMI account.
                </span>
              ) : null}
            </div>

            <div className="git-settings-grid">
              <Select
                id="settings-github-repository"
                labelText="Repository"
                value={selectedGitHubRepository}
                onChange={event => {
                  const fullName = event.target.value;
                  setSelectedGitHubRepository(fullName);
                  const repository = gitHubRepositories.find(item => item.fullName === fullName);
                  if (repository) {
                    setGitBranch(repository.defaultBranch);
                  }
                }}
                disabled={gitHubRepositories.length === 0}
              >
                <SelectItem value="" text={gitHubRepositories.length === 0 ? 'Refresh repositories first' : 'Select repository'} />
                {gitHubRepositories.map(repository => (
                  <SelectItem
                    key={repository.id}
                    value={repository.fullName}
                    text={`${repository.fullName}${repository.private ? ' private' : ''}`}
                  />
                ))}
              </Select>
              <TextInput
                id="settings-git-branch"
                labelText="Branch"
                placeholder="main"
                value={gitBranch}
                onChange={event => setGitBranch(event.target.value)}
              />
              <TextInput
                id="settings-git-path-prefix"
                labelText="Path prefix"
                placeholder="optional/subdir"
                value={gitPathPrefix}
                onChange={event => setGitPathPrefix(event.target.value)}
              />
              <TextInput
                id="settings-git-author-name"
                labelText="Commit author name"
                placeholder="XMI Workbench"
                value={gitAuthorName}
                onChange={event => setGitAuthorName(event.target.value)}
              />
              <TextInput
                id="settings-git-author-email"
                labelText="Commit author email"
                placeholder="xmi_viewer@163.com"
                value={gitAuthorEmail}
                onChange={event => setGitAuthorEmail(event.target.value)}
              />
            </div>

            <div className="git-settings-actions">
              <Toggle
                id="settings-git-auto-sync"
                labelA="Manual"
                labelB="Auto sync"
                labelText="Sync mode"
                toggled={gitAutoSync}
                onToggle={setGitAutoSync}
              />
              <Button
                kind="primary"
                renderIcon={Save}
                onClick={() => void handleSaveGitHubRepository()}
                disabled={!selectedGitHubRepository || gitAction !== null}
              >
                {gitAction === 'save' ? 'Saving...' : 'Save repository'}
              </Button>
            </div>

            {gitError ? (
              <InlineNotification
                lowContrast
                kind="error"
                title="Git failed"
                subtitle={gitError}
                onCloseButtonClick={() => setGitError(null)}
              />
            ) : null}

            {gitInfo ? (
              <InlineNotification
                lowContrast
                kind="success"
                title="Git"
                subtitle={gitInfo}
                onCloseButtonClick={() => setGitInfo(null)}
              />
            ) : null}
          </Stack>
        </div>
      </Modal>

      <Modal
        open={isUploadModalOpen}
        passiveModal
        size="sm"
        modalLabel="Input"
        modalHeading="Upload dataset"
        onRequestClose={() => {
          if (!isUploading) {
            setIsUploadModalOpen(false);
          }
        }}
        preventCloseOnClickOutside={isUploading}
      >
        <div className="upload-modal">
          <Stack gap={5}>
            {isUploading ? <InlineLoading description="Parsing dataset" /> : null}

            <div className="upload-modal__primary">
              <div>
                <p className="upload-modal__section-title">Dataset source</p>
                <p className="upload-modal__helper">Drop an archive, or choose a folder exported from a PDS. Folder file names become member names.</p>
              </div>
              <div className="upload-modal__source-grid">
                <div
                  className="upload-source-card"
                  onDragOver={event => {
                    event.preventDefault();
                  }}
                  onDrop={event => {
                    event.preventDefault();
                    if (!isUploading) {
                      handleXmiFileSelection(event.dataTransfer.files);
                    }
                  }}
                >
                  <Button
                    kind="secondary"
                    size="sm"
                    renderIcon={Upload}
                    disabled={isUploading}
                    onClick={() => xmiFileInputRef.current?.click()}
                  >
                    Choose XMI/XMIT
                  </Button>
                  <span>Drop or choose one transmitted archive file.</span>
                  <input
                    ref={xmiFileInputRef}
                    className="upload-modal__file-input"
                    type="file"
                    accept=".xmi,.xmit"
                    onChange={event => handleXmiFileSelection(event.target.files)}
                  />
                </div>

                <div className="upload-source-card">
                  <Button
                    kind="secondary"
                    size="sm"
                    renderIcon={Upload}
                    disabled={isUploading}
                    onClick={() => txtFolderInputRef.current?.click()}
                  >
                    Choose PDS folder
                  </Button>
                  <span>Imports every file in the folder as a simulated member, including files without extensions.</span>
                  <input
                    ref={txtFolderInputRef}
                    className="upload-modal__file-input"
                    type="file"
                    multiple
                    onChange={event => handleTxtFolderSelection(event.target.files)}
                    {...DIRECTORY_PICKER_PROPS}
                  />
                </div>
              </div>

              <details className="upload-modal__encoding-details">
                <summary>EBCDIC encoding: {formatEncodingLabel(encoding)}</summary>
                <Select
                  id="encoding"
                  labelText="EBCDIC encoding"
                  helperText="Only affects XMI/XMIT archive decoding. Folder imports are read as UTF-8 text."
                  value={encoding}
                  onChange={event => setEncoding(event.target.value)}
                >
                  {ENCODING_OPTIONS.map(option => (
                    <SelectItem key={option} value={option} text={formatEncodingLabel(option)} />
                  ))}
                </Select>
              </details>
            </div>

            {error ? (
              <InlineNotification
                lowContrast
                kind="error"
                title="Upload failed"
                subtitle={error}
                onCloseButtonClick={() => setError(null)}
              />
            ) : null}

          </Stack>
        </div>
      </Modal>
    </div>
  );
}

function AuthPanel({
  mode,
  email,
  password,
  code,
  devCode,
  error,
  info,
  isSubmitting,
  onModeChange,
  onEmailChange,
  onPasswordChange,
  onCodeChange,
  onLogin,
  onRequestCode,
  onRegister
}: {
  mode: 'login' | 'register';
  email: string;
  password: string;
  code: string;
  devCode: string | null;
  error: string | null;
  info: string | null;
  isSubmitting: boolean;
  onModeChange: (mode: 'login' | 'register') => void;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onCodeChange: (value: string) => void;
  onLogin: () => void;
  onRequestCode: () => void;
  onRegister: () => void;
}) {
  return (
    <div className="auth-shell">
      <Tile className="auth-panel">
        <Stack gap={6}>
          <div>
            <p className="panel-label">XMI Workbench</p>
            <h1>{mode === 'login' ? 'Sign in' : 'Create account'}</h1>
            <p className="auth-support">
              Verification emails and support: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
            <div className="auth-links">
              <a href="/guide">Guide</a>
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
            </div>
          </div>

          <div className="auth-switch">
            <Button kind={mode === 'login' ? 'primary' : 'secondary'} onClick={() => onModeChange('login')}>
              Login
            </Button>
            <Button kind={mode === 'register' ? 'primary' : 'secondary'} onClick={() => onModeChange('register')}>
              Register
            </Button>
          </div>

          <TextInput
            id="auth-email"
            labelText="Email"
            type="email"
            value={email}
            onChange={event => onEmailChange(event.target.value)}
          />
          <PasswordInput
            id="auth-password"
            labelText="Password"
            value={password}
            onChange={event => onPasswordChange(event.target.value)}
          />

          {mode === 'register' ? (
            <>
              <div className="auth-code-row">
                <TextInput
                  id="auth-code"
                  labelText="Verification code"
                  value={code}
                  onChange={event => onCodeChange(event.target.value)}
                />
                <Button kind="secondary" onClick={onRequestCode} disabled={isSubmitting}>
                  Send code
                </Button>
              </div>
              {devCode ? <CodeSnippet type="single">Dev code: {devCode}</CodeSnippet> : null}
            </>
          ) : null}

          {error ? <InlineNotification lowContrast kind="error" title="Authentication failed" subtitle={error} hideCloseButton /> : null}
          {info ? <InlineNotification lowContrast kind="success" title="Verification" subtitle={info} hideCloseButton /> : null}

          <Button
            renderIcon={Login}
            onClick={mode === 'login' ? onLogin : onRegister}
            disabled={isSubmitting}
          >
            {mode === 'login' ? 'Login' : 'Complete registration'}
          </Button>
        </Stack>
      </Tile>
    </div>
  );
}

function PublicPage({ pageKey }: { pageKey: PublicPageKey }) {
  const page = PUBLIC_PAGE_CONTENT[pageKey];

  return (
    <div className="public-shell">
      <header className="public-topbar">
        <div>
          <p className="eyebrow">XMI Workbench</p>
          <h1>{page.title}</h1>
        </div>
        <nav className="public-nav" aria-label="Public pages">
          <a className={pageKey === 'guide' ? 'public-nav__active' : ''} href="/guide">Guide</a>
          <a className={pageKey === 'privacy' ? 'public-nav__active' : ''} href="/privacy">Privacy</a>
          <a className={pageKey === 'terms' ? 'public-nav__active' : ''} href="/terms">Terms</a>
          <a href="/">App</a>
        </nav>
      </header>

      <main className="public-page">
        <section className="public-intro">
          <Tag type="blue">{page.label}</Tag>
          <p>{page.intro}</p>
          <span>Updated {page.updatedAt}</span>
        </section>

        <div className="public-section-list">
          {page.sections.map(section => (
            <section className="public-section" key={section.title}>
              <h2>{section.title}</h2>
              <ul>
                {section.items.map(item => <li key={item}>{item}</li>)}
              </ul>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}

function AdminManagementPanel({
  activeView,
  data,
  error,
  info,
  isLoading,
  isSavingPrompt,
  promptProviderId,
  promptBaseUrl,
  promptModel,
  promptScope,
  promptText,
  onViewChange,
  onRefresh,
  onErrorClose,
  onInfoClose,
  onPromptProviderIdChange,
  onPromptBaseUrlChange,
  onPromptModelChange,
  onPromptScopeChange,
  onPromptTextChange,
  onPromptSave
}: {
  activeView: AdminPanelView;
  data: AdminOperationsResponse | null;
  error: string | null;
  info: string | null;
  isLoading: boolean;
  isSavingPrompt: boolean;
  promptProviderId: string;
  promptBaseUrl: string;
  promptModel: string;
  promptScope: 'member' | 'dataset';
  promptText: string;
  onViewChange: (view: AdminPanelView) => void;
  onRefresh: () => void;
  onErrorClose: () => void;
  onInfoClose: () => void;
  onPromptProviderIdChange: (value: string) => void;
  onPromptBaseUrlChange: (value: string) => void;
  onPromptModelChange: (value: string) => void;
  onPromptScopeChange: (value: 'member' | 'dataset') => void;
  onPromptTextChange: (value: string) => void;
  onPromptSave: () => void;
}) {
  const navItems: Array<{ key: AdminPanelView; label: string; count?: number }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'users', label: 'Users', count: data?.users.length },
    { key: 'uploads', label: 'Datasets', count: data?.uploads.length },
    { key: 'prompt', label: 'Prompt' },
    { key: 'logs', label: 'Logs', count: (data?.recentErrors.length ?? 0) + (data?.recentAuditEvents.length ?? 0) }
  ];

  return (
    <div className="admin-operations-modal">
      <Stack gap={5}>
        <div className="admin-operations-toolbar">
          <div className="admin-operations-status">
            <Tag type={data?.health.ok ? 'green' : 'red'}>{data?.health.ok ? 'healthy' : 'unavailable'}</Tag>
            {data ? <Tag type="cool-gray">{formatDateTime(data.generatedAt)}</Tag> : null}
            {data ? <Tag type="blue">{formatDuration(data.health.uptimeSeconds)}</Tag> : null}
          </div>
          <Button kind="secondary" size="sm" onClick={onRefresh} disabled={isLoading}>
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        <div className="admin-nav">
          {navItems.map(item => (
            <button
              type="button"
              key={item.key}
              className={`admin-nav__item${activeView === item.key ? ' admin-nav__item--active' : ''}`}
              onClick={() => onViewChange(item.key)}
            >
              <span>{item.label}</span>
              {typeof item.count === 'number' ? <strong>{item.count}</strong> : null}
            </button>
          ))}
        </div>

        {error ? (
          <InlineNotification
            lowContrast
            kind="error"
            title="Admin failed"
            subtitle={error}
            onCloseButtonClick={onErrorClose}
          />
        ) : null}

        {info ? (
          <InlineNotification
            lowContrast
            kind="success"
            title="Admin"
            subtitle={info}
            onCloseButtonClick={onInfoClose}
          />
        ) : null}

        {isLoading && !data ? <InlineLoading description="Loading admin data" /> : null}

        {data ? (
          <>
            {activeView === 'overview' ? <AdminOverview data={data} /> : null}
            {activeView === 'users' ? <AdminUsers data={data} /> : null}
            {activeView === 'uploads' ? <AdminUploads data={data} /> : null}
            {activeView === 'prompt' ? (
              <AdminPromptEditor
                data={data}
                providerId={promptProviderId}
                baseUrl={promptBaseUrl}
                model={promptModel}
                scope={promptScope}
                prompt={promptText}
                isSaving={isSavingPrompt}
                onProviderIdChange={onPromptProviderIdChange}
                onBaseUrlChange={onPromptBaseUrlChange}
                onModelChange={onPromptModelChange}
                onScopeChange={onPromptScopeChange}
                onPromptChange={onPromptTextChange}
                onSave={onPromptSave}
              />
            ) : null}
            {activeView === 'logs' ? <AdminLogs data={data} /> : null}
          </>
        ) : null}
      </Stack>
    </div>
  );
}

function AdminOverview({ data }: { data: AdminOperationsResponse }) {
  return (
    <div className="admin-operations-grid">
      <Tile className="panel admin-operations-card">
        <p className="panel-label">Runtime</p>
        <h3>{data.build.version} · {formatCommit(data.build.commit)}</h3>
        <div className="admin-operations-kv">
          <span>Node {data.health.nodeVersion}</span>
          <span>{data.health.environment}</span>
          <span>PID {data.health.pid}</span>
          <span>{formatDuration(data.health.uptimeSeconds)}</span>
        </div>
        <p className="admin-operations-note">{data.health.appOrigin}</p>
      </Tile>

      <Tile className="panel admin-operations-card">
        <p className="panel-label">Users</p>
        <h3>{data.auth.userCount}</h3>
        <div className="admin-operations-kv">
          <span>{data.auth.activeSessionCount} sessions</span>
          <span>{data.auth.pendingVerificationCodeCount} pending codes</span>
          <span>{data.auth.adminConfigured ? 'admin configured' : 'admin missing'}</span>
        </div>
      </Tile>

      <Tile className="panel admin-operations-card">
        <p className="panel-label">Datasets</p>
        <h3>{data.uploads.length}</h3>
        <div className="admin-operations-kv">
          <span>{data.databases.history.uploadCount} uploads</span>
          <span>{data.databases.history.analysisCount} AI outputs</span>
          <span>{data.storage.uploadStorage.totalFileCount} retained files</span>
        </div>
      </Tile>

      <Tile className="panel admin-operations-card">
        <p className="panel-label">Storage</p>
        <h3>{formatBytes(data.storage.totalBytes)}</h3>
        <div className="admin-operations-kv">
          <span>{formatBytes(data.storage.uploadStorage.totalBytes)} uploads</span>
          <span>{data.storage.databaseFiles.length} data files</span>
        </div>
      </Tile>
    </div>
  );
}

function AdminUsers({ data }: { data: AdminOperationsResponse }) {
  return (
    <section className="admin-operations-section">
      <div className="admin-operations-section__head">
        <div>
          <p className="panel-label">Registered users</p>
          <h3>Email accounts</h3>
        </div>
        <Tag type="blue">{data.users.length}</Tag>
      </div>
      <div className="admin-table">
        <div className="admin-table__row admin-table__row--head">
          <span>Email</span>
          <span>Created</span>
          <span>Role</span>
          <span>Sessions</span>
        </div>
        {data.users.map(user => (
          <div className="admin-table__row" key={user.id}>
            <span>{user.email}</span>
            <span>{user.createdAt === 'env' ? 'environment' : formatDateTime(user.createdAt)}</span>
            <span>{user.isAdmin ? 'admin' : 'user'}</span>
            <span>{user.activeSessionCount}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function AdminUploads({ data }: { data: AdminOperationsResponse }) {
  return (
    <section className="admin-operations-section">
      <div className="admin-operations-section__head">
        <div>
          <p className="panel-label">Uploaded datasets</p>
          <h3>History inventory</h3>
        </div>
        <Tag type="cyan">{data.uploads.length}</Tag>
      </div>
      <div className="admin-table admin-table--uploads">
        <div className="admin-table__row admin-table__row--head">
          <span>Dataset</span>
          <span>File</span>
          <span>Size</span>
          <span>Members</span>
          <span>Owner</span>
          <span>Uploaded</span>
        </div>
        {data.uploads.length === 0 ? (
          <p className="admin-operations-empty">No uploaded datasets.</p>
        ) : data.uploads.map(upload => (
          <div className="admin-table__row" key={upload.id}>
            <span>{upload.datasetName}</span>
            <span>{upload.fileName}</span>
            <span>{formatBytes(upload.originalFileSize ?? 0)}</span>
            <span>{upload.memberCount}</span>
            <span>{emailForUserId(data, upload.userId)}</span>
            <span>{formatDateTime(upload.uploadedAt)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function AdminPromptEditor({
  data,
  providerId,
  baseUrl,
  model,
  scope,
  prompt,
  isSaving,
  onProviderIdChange,
  onBaseUrlChange,
  onModelChange,
  onScopeChange,
  onPromptChange,
  onSave
}: {
  data: AdminOperationsResponse;
  providerId: string;
  baseUrl: string;
  model: string;
  scope: 'member' | 'dataset';
  prompt: string;
  isSaving: boolean;
  onProviderIdChange: (value: string) => void;
  onBaseUrlChange: (value: string) => void;
  onModelChange: (value: string) => void;
  onScopeChange: (value: 'member' | 'dataset') => void;
  onPromptChange: (value: string) => void;
  onSave: () => void;
}) {
  return (
    <section className="admin-operations-section">
      <div className="admin-operations-section__head">
        <div>
          <p className="panel-label">LLM prompt</p>
          <h3>Global analysis instructions</h3>
        </div>
        <div className="admin-operations-kv">
          <Tag type={data.llmPrompt.apiKeyConfigured ? 'green' : 'red'}>{data.llmPrompt.apiKeyConfigured ? 'key configured' : 'key missing'}</Tag>
          <Tag type={data.llmPrompt.customPromptConfigured ? 'blue' : 'cool-gray'}>{data.llmPrompt.customPromptConfigured ? 'custom prompt' : 'built-in prompt'}</Tag>
          <Tag type="cool-gray">{formatDateTime(data.llmPrompt.updatedAt)}</Tag>
        </div>
      </div>
      <div className="admin-prompt-grid">
        <TextInput id="admin-prompt-provider" labelText="Provider id" value={providerId} onChange={event => onProviderIdChange(event.target.value)} />
        <TextInput id="admin-prompt-base-url" labelText="Base URL" value={baseUrl} onChange={event => onBaseUrlChange(event.target.value)} />
        <TextInput id="admin-prompt-model" labelText="Model" value={model} onChange={event => onModelChange(event.target.value)} />
        <Select id="admin-prompt-scope" labelText="Default scope" value={scope} onChange={event => onScopeChange(event.target.value === 'dataset' ? 'dataset' : 'member')}>
          <SelectItem value="member" text="Member" />
          <SelectItem value="dataset" text="Dataset" />
        </Select>
      </div>
      <TextArea
        id="admin-prompt-text"
        labelText="Prompt"
        rows={12}
        value={prompt}
        onChange={event => onPromptChange(event.target.value)}
      />
      <div className="admin-operations-toolbar">
        <p className="admin-operations-note">API keys stay server-side. Leave prompt empty to use the built-in language and dataset prompts.</p>
        <Button renderIcon={Save} onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save prompt'}
        </Button>
      </div>
    </section>
  );
}

function AdminLogs({ data }: { data: AdminOperationsResponse }) {
  return (
    <div className="admin-operations-grid">
      <section className="admin-operations-section">
        <div className="admin-operations-section__head">
          <div>
            <p className="panel-label">Recent errors</p>
            <h3>Errors and warnings</h3>
          </div>
          <Tag type="red">{data.recentErrors.length}</Tag>
        </div>
        {data.recentErrors.length === 0 ? (
          <p className="admin-operations-empty">No recent API or process errors.</p>
        ) : (
          <div className="admin-operations-list">
            {data.recentErrors.map(item => (
              <article className="admin-operations-log" key={item.id}>
                <div className="admin-operations-log__head">
                  <strong>{item.source}</strong>
                  <div className="admin-operations-kv">
                    <span>{item.severity}</span>
                    <span>{formatDateTime(item.createdAt)}</span>
                  </div>
                </div>
                <p>{item.message}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="admin-operations-section">
        <div className="admin-operations-section__head">
          <div>
            <p className="panel-label">Audit</p>
            <h3>Recent actions</h3>
          </div>
          <Tag type="purple">{data.recentAuditEvents.length}</Tag>
        </div>
        {data.recentAuditEvents.length === 0 ? (
          <p className="admin-operations-empty">No audit events recorded yet.</p>
        ) : (
          <div className="admin-operations-list">
            {data.recentAuditEvents.map(item => (
              <article className="admin-operations-log" key={item.id}>
                <div className="admin-operations-log__head">
                  <strong>{item.action}</strong>
                  <div className="admin-operations-kv">
                    <span>{item.outcome}</span>
                    <span>{formatDateTime(item.createdAt)}</span>
                  </div>
                </div>
                <div className="admin-operations-kv">
                  {item.actorEmail ? <span>{item.actorEmail}</span> : null}
                  {item.targetType && item.targetId ? <span>{item.targetType}: {item.targetId}</span> : null}
                </div>
                {item.message ? <p>{item.message}</p> : null}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function AnalysisBody({ body }: { body: string }) {
  const blocks = body
    .replace(/\r\n/g, '\n')
    .trim()
    .split(/\n{2,}/)
    .filter(Boolean);

  if (blocks.length === 0) {
    return <p className="analysis-block">No output.</p>;
  }

  return (
    <div className="analysis-blocks">
      {blocks.map((block, index) => {
        if (/^```/.test(block.trim())) {
          return <pre className="analysis-code" key={index}>{stripCodeFence(block)}</pre>;
        }

        const lines = block.split('\n');
        if (lines.every(line => /^\s*[-*]\s+/.test(line))) {
          return (
            <ul className="analysis-list-block" key={index}>
              {lines.map((line, lineIndex) => <li key={lineIndex}>{line.replace(/^\s*[-*]\s+/, '')}</li>)}
            </ul>
          );
        }

        if (lines.every(line => /^\s*\d+\.\s+/.test(line))) {
          return (
            <ol className="analysis-list-block" key={index}>
              {lines.map((line, lineIndex) => <li key={lineIndex}>{line.replace(/^\s*\d+\.\s+/, '')}</li>)}
            </ol>
          );
        }

        return (
          <div className="analysis-block" key={index}>
            {lines.map((line, lineIndex) => (
              <p key={lineIndex}>{renderInlineMarkdown(line)}</p>
            ))}
          </div>
        );
      })}
    </div>
  );
}

async function apiJson<T>(url: string, init?: RequestInit): Promise<T> {
  return apiJsonWithTimeout<T>(url, init);
}

async function apiJsonWithTimeout<T>(url: string, init?: RequestInit, timeoutMs?: number): Promise<T> {
  const response = timeoutMs
    ? await fetchWithTimeout(url, {
      ...init,
      credentials: 'include',
      headers: {
        ...(init?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...init?.headers
      }
    }, timeoutMs)
    : await fetch(url, {
      ...init,
      credentials: 'include',
      headers: {
        ...(init?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...init?.headers
      }
    });

  const payload = await parseApiResponse(response);
  if (!response.ok) {
    throw new Error(typeof payload.error === 'string' ? payload.error : 'Request failed.');
  }
  return payload as T;
}

async function parseApiResponse(response: Response): Promise<Record<string, unknown>> {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return await response.json() as Record<string, unknown>;
  }

  const text = await response.text();
  return {
    error: buildNonJsonErrorMessage(response, text)
  };
}

function buildNonJsonErrorMessage(response: Response, body: string): string {
  if (response.status === 413) {
    return 'Upload rejected before reaching the app. Increase the reverse proxy body-size limit, for example Nginx `client_max_body_size`.';
  }

  const trimmed = body.replace(/\s+/g, ' ').trim();
  if (trimmed.startsWith('<')) {
    return `Server returned an HTML error page (${response.status} ${response.statusText || 'error'}) instead of JSON.`;
  }

  if (trimmed) {
    return trimmed.length > 240 ? `${trimmed.slice(0, 237)}...` : trimmed;
  }

  return `Request failed with status ${response.status}.`;
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      ...init,
      signal: controller.signal
    });
  } catch (fetchError) {
    if (fetchError instanceof DOMException && fetchError.name === 'AbortError') {
      throw new Error('Parsing timed out after 60 seconds.');
    }
    throw fetchError;
  } finally {
    window.clearTimeout(timeout);
  }
}

function formatAnalysisTitle(analysis: LlmAnalysisResult): string {
  if (analysis.kind === 'dataset_overview') {
    return 'Dataset overview';
  }
  if (analysis.kind === 'member_summary') {
    return analysis.memberName ? `${analysis.memberName} summary` : 'Member summary';
  }
  if (analysis.memberName) {
    return `Member ${analysis.memberName}`;
  }
  return 'Member analysis';
}

function formatAnalysisKind(kind: LlmAnalysisResult['kind']): string {
  if (kind === 'dataset_overview') {
    return 'Dataset overview';
  }
  if (kind === 'member_summary') {
    return 'Member summary';
  }
  return 'Member detail';
}

function gitSyncStatusTag(status: GitSyncJob['status']): 'blue' | 'green' | 'red' | 'warm-gray' {
  if (status === 'completed') {
    return 'green';
  }
  if (status === 'failed') {
    return 'red';
  }
  if (status === 'running') {
    return 'blue';
  }
  return 'warm-gray';
}

function buildGitCommitUrl(repositoryHtmlUrl: string, commitHash: string | undefined): string | null {
  if (!repositoryHtmlUrl || !commitHash) {
    return null;
  }

  try {
    return new URL(`commit/${commitHash}`, `${repositoryHtmlUrl.replace(/\/+$/, '')}/`).toString();
  } catch {
    return null;
  }
}

function formatScanTaskTitle(task: DatasetScanTask): string {
  if (task.status === 'queued') {
    return 'Dataset scan queued';
  }
  if (task.status === 'running') {
    return 'Dataset scan running';
  }
  if (task.status === 'completed') {
    return 'Dataset scan completed';
  }
  return 'Dataset scan failed';
}

function formatScanTaskSubtitle(task: DatasetScanTask): string {
  if (task.status === 'failed') {
    return task.errorMessage || 'The dataset scan stopped before completion.';
  }
  const total = Math.max(task.totalSteps, 1);
  const processed = Math.min(task.processedSteps, total);
  const base = `${processed}/${total} steps, ${task.completedAnalyses} saved output${task.completedAnalyses === 1 ? '' : 's'}.`;
  if (task.status === 'completed') {
    return `${base} Finished at ${formatDateTime(task.completedAt || task.updatedAt)}.`;
  }
  if (task.status === 'running') {
    return `${base} Controlled scan: selected members only, truncated source, serial LLM calls.`;
  }
  return `${base} Waiting for the controlled scan worker to start.`;
}

function formatDateTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
}

function formatBuildTime(value: string): string {
  if (!value || value === 'unknown') {
    return 'unknown';
  }
  return formatDateTime(value);
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return '0 bytes';
  }
  if (value >= 1024 * 1024 * 1024) {
    return `${(value / 1024 / 1024 / 1024).toFixed(1)} GB`;
  }
  if (value >= 1024 * 1024) {
    return `${(value / 1024 / 1024).toFixed(1)} MB`;
  }
  if (value >= 1024) {
    return `${Math.round(value / 1024)} KB`;
  }
  return `${value} bytes`;
}

function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return 'unknown';
  }
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${Math.floor(totalSeconds)}s`;
}

function formatCommit(value: string): string {
  if (!value || value === 'unknown') {
    return 'unknown';
  }
  return value.length > 8 ? value.slice(0, 8) : value;
}

function formatEncodingLabel(value: string): string {
  if (value === 'auto') {
    return 'Auto detect';
  }
  if (value === 'CP037') {
    return 'CP037 - US/Canada';
  }
  if (value === 'CP1047') {
    return 'CP1047 - Open Systems Latin';
  }
  if (value === 'CP1140') {
    return 'CP1140 - US/Canada Euro';
  }
  if (value === 'CP935') {
    return 'CP935 - Simplified Chinese';
  }
  if (value === 'CP937') {
    return 'CP937 - Traditional Chinese';
  }
  if (value === 'CP1388') {
    return 'CP1388 - Simplified Chinese';
  }
  if (value === 'CP930') {
    return 'CP930 - Japanese';
  }
  if (value === 'CP939') {
    return 'CP939 - Japanese';
  }
  if (value === 'CP1390') {
    return 'CP1390 - Japanese';
  }
  return value;
}

function formatMemberOptionLabel(
  name: string,
  language: string,
  recordCount: number,
  nameMatched: boolean,
  contentMatched: boolean
): string {
  const matchTags: string[] = [];
  if (nameMatched) {
    matchTags.push('name');
  }
  if (contentMatched) {
    matchTags.push('content');
  }
  return `${name} - ${language.toUpperCase()} - ${recordCount} rows${matchTags.length > 0 ? ` - match: ${matchTags.join('/')}` : ''}`;
}

function loadSavedMemberFilters(): SavedMemberFilter[] {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(SAVED_MEMBER_FILTERS_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map(item => normalizeSavedMemberFilter(item))
      .filter((item): item is SavedMemberFilter => item !== null)
      .slice(0, 12);
  } catch {
    return [];
  }
}

function persistSavedMemberFilters(filters: SavedMemberFilter[]): void {
  try {
    window.localStorage.setItem(SAVED_MEMBER_FILTERS_KEY, JSON.stringify(filters.slice(0, 12)));
  } catch {
    // Local storage can be unavailable in private browsing or locked-down deployments.
  }
}

function normalizeSavedMemberFilter(value: unknown): SavedMemberFilter | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const candidate = value as Partial<SavedMemberFilter>;
  if (typeof candidate.query !== 'string' || !candidate.query.trim()) {
    return null;
  }
  const query = candidate.query.trim();
  return {
    id: typeof candidate.id === 'string' && candidate.id ? candidate.id : createClientId(),
    label: typeof candidate.label === 'string' && candidate.label.trim()
      ? candidate.label.trim().slice(0, 32)
      : query.slice(0, 28),
    query,
    searchContent: candidate.searchContent === true
  };
}

function createClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function buildMemberSearchIndexEntry(member: XmiMember): MemberSearchIndexEntry {
  const lowerText = member.text.toLowerCase();
  return {
    member,
    lowerName: member.name.toLowerCase(),
    lowerText,
    lowerLines: lowerText.split('\n')
  };
}

function findMatchingLineNumbersFromIndex(entry: MemberSearchIndexEntry, query: string): number[] {
  if (!query) {
    return [];
  }
  if (!entry.lowerText.includes(query)) {
    return [];
  }
  const matches: number[] = [];
  for (let index = 0; index < entry.lowerLines.length; index += 1) {
    if (entry.lowerLines[index].includes(query)) {
      matches.push(index + 1);
    }
  }
  return matches;
}

function toFileList(files: File[]): FileList {
  const dataTransfer = new DataTransfer();
  for (const file of files) {
    dataTransfer.items.add(file);
  }
  return dataTransfer.files;
}

function getFileRelativePath(file: File): string {
  const candidate = file as File & { webkitRelativePath?: string };
  return candidate.webkitRelativePath || file.name;
}

function inferDatasetNameFromFolderFiles(files: File[]): string {
  const firstRelativePath = getFileRelativePath(files[0] as File);
  const folderName = firstRelativePath.includes('/')
    ? firstRelativePath.split('/')[0]
    : '';
  return sanitizeClientDatasetName(folderName || 'TXT_FOLDER');
}

function sanitizeClientDatasetName(value: string): string {
  const cleaned = value
    .trim()
    .replace(/[^A-Za-z0-9#$@._-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
  return cleaned || 'TXT_FOLDER';
}

function isParserIssue(warning: string): boolean {
  const normalized = warning.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  if (normalized.startsWith('auto-detected ebcdic encoding')) {
    return false;
  }
  if (normalized.startsWith('recovered ') && normalized.includes('member(s) from iebcopy')) {
    return false;
  }
  return true;
}

function formatAnalysisSections(content: string): Array<{ title: string; body: string }> {
  const normalized = content.replace(/\r\n/g, '\n').trim();
  if (!normalized) {
    return [{ title: 'Output', body: '' }];
  }

  const matches = [...normalized.matchAll(/^(#{1,3}\s*.+?|\*\*[^*]+\*\*|[A-Za-z][A-Za-z0-9 /&()-]+:)\s*$/gm)];
  if (matches.length === 0) {
    return [{ title: 'Output', body: normalized }];
  }

  return matches.map((match, index) => {
    const start = match.index ?? 0;
    const end = matches[index + 1]?.index ?? normalized.length;
    const block = normalized.slice(start, end).trim();
    const lines = block.split('\n');
    const title = lines[0]
      .replace(/^#{1,3}\s*/, '')
      .replace(/^\*\*|\*\*$/g, '')
      .replace(/:\s*$/, '')
      .trim() || 'Output';
    const body = lines.slice(1).join('\n').trim();
    return {
      title,
      body: body || block
    };
  });
}

function stripCodeFence(block: string): string {
  return block
    .replace(/^```[a-zA-Z0-9_-]*\n?/, '')
    .replace(/\n?```$/, '')
    .trim();
}

function renderInlineMarkdown(text: string) {
  const parts: Array<ReturnType<typeof renderInlineStrong> | string> = [];
  const pattern = /\*\*([^*]+)\*\*|`([^`]+)`/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1]) {
      parts.push(renderInlineStrong(`${match.index}-strong`, match[1]));
    } else if (match[2]) {
      parts.push(<code key={`${match.index}-code`}>{match[2]}</code>);
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? <>{parts}</> : text;
}

function renderInlineStrong(key: string, text: string) {
  return <strong key={key}>{text}</strong>;
}

function emailForUserId(data: AdminOperationsResponse, userId: string): string {
  return data.users.find(user => user.id === userId)?.email ?? userId;
}

function getPublicPageKey(pathname: string): PublicPageKey | null {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/guide') {
    return 'guide';
  }
  if (normalized === '/privacy') {
    return 'privacy';
  }
  if (normalized === '/terms') {
    return 'terms';
  }
  return null;
}

function getWorkspacePageKey(pathname: string): WorkspacePageKey {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/workspace') {
    return 'workspace';
  }
  return 'datasets';
}
